import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { Camera, Printer, X, Check, CheckCircle, RefreshCw, ChevronLeft, ChevronRight, Wifi, WifiOff, RotateCcw, Smartphone, AlertTriangle, AlertCircle, MessageCircle } from 'lucide-react';

// Import error handling utilities
import { 
  parseError, 
  retryWithBackoff, 
  NetworkMonitor, 
  recoverCameraStream,
  SessionTimeoutManager,
  ERROR_TYPES 
} from '../utils/errorHandling';
import { 
  ErrorBoundary, 
  ErrorDisplay, 
  NetworkStatusBanner,
  SessionTimeoutWarning,
  ErrorConsole,
  useErrorBuffer
} from '../components/ErrorComponents';
import { cachePhoto, getCachedSession, clearCachedSession } from '../utils/photoCache';
import boothLogger from '../utils/boothLogger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WS_URL = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://') + '/api';

// Kiosk Screens
// Mirror of backend resolve_active_pricing(): merges the currently-active
// busy-hour schedule over base pricing. Booth local clock (IST venues),
// first matching schedule wins, overnight windows (22:00-02:00) supported.
const resolveActivePricing = (pricing) => {
  if (!pricing?.schedules?.length) return pricing;
  const now = new Date();
  const cur = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  const dow = (now.getDay() + 6) % 7; // 0 = Monday, matching backend weekday()
  for (const s of pricing.schedules) {
    if (s.days?.length && !s.days.includes(dow)) continue;
    if (!s.start || !s.end) continue;
    const active = s.start <= s.end ? (cur >= s.start && cur < s.end) : (cur >= s.start || cur < s.end);
    if (active) {
      const merged = { ...pricing };
      ['one_unit_price', 'two_unit_price', 'three_unit_price'].forEach((k) => {
        if (s[k] !== null && s[k] !== undefined && s[k] !== '') merged[k] = s[k];
      });
      if (s.enabled_quantities?.length) merged.enabled_quantities = s.enabled_quantities;
      return merged;
    }
  }
  return pricing;
};

const SCREENS = {
  PAIRING: 'pairing',
  RECONNECTING: 'reconnecting',  // Previously-paired booth trying to re-establish connection
  IDLE: 'idle',
  QUANTITY_SELECT: 'quantity_select',
  PAYMENT: 'payment',
  PHOTO_CAPTURE: 'photo_capture',
  PHOTO_REVIEW: 'photo_review',  // NEW: Review each photo
  PREVIEW: 'preview',
  PRINTING: 'printing',
  COMPLETE: 'complete',
  EXIT_PROMPT: 'exit_prompt',
  DISCONNECTED: 'disconnected',
  ERROR: 'error'  // NEW: Error screen
};

// Shared QR loading overlay — dedup of two identical inline copies (iter22
// review comment). Rendered while (a) no qr_code from backend yet OR (b) img
// still fetching from Razorpay CDN.
const QRLoadingOverlay = () => (
  <div
    className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-2xl"
    data-testid="qr-loading-overlay"
  >
    <RefreshCw className="w-10 h-10 text-charcoal-800/40 animate-spin mb-3" />
    <span className="text-sm text-charcoal-800/50">Preparing your QR…</span>
  </div>
);

// Error boundary — catches any render-time exception from the kiosk tree so
// a random crash shows a recoverable screen instead of a white blank. This is
// the "why did the booth go blank?" defence.
class KioskErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Kiosk crash:', error, info);
    // Log to booth activity so the exact renderer crash surfaces in the
    // admin Activity Log alongside every preceding event.
    try {
      boothLogger.error('error', 'renderer_crash', error?.message || 'Unknown render crash', {
        stack: error?.stack || '',
        component_stack: info?.componentStack || '',
        url: window.location.href,
      });
      boothLogger.flush();
    } catch (_e) { /* noop */ }
    // Fire-and-forget crash telemetry — surfaces silent React render crashes
    // in the admin Fleet dashboard. Failures are swallowed (we don't want
    // the boundary itself to throw). The endpoint is public/no-auth but
    // rate-limited server-side.
    try {
      const kioskId = localStorage.getItem('piczoo_kiosk_id');
      if (kioskId && process.env.REACT_APP_BACKEND_URL) {
        // Best-effort screen inference from the URL path
        const url = window.location.href || '';
        const screenGuess = (url.match(/[?&]screen=([^&#]+)/) || [])[1] || '';
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kiosks/${kioskId}/crash-report`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          keepalive: true,
          body: JSON.stringify({
            message: (error && (error.message || String(error))) || 'Unknown error',
            stack: (error && error.stack) || '',
            component_stack: (info && info.componentStack) || '',
            url,
            user_agent: navigator.userAgent || '',
            screen: screenGuess,
            kiosk_version: process.env.REACT_APP_KIOSK_VERSION || '',
          }),
        }).catch(() => {});
      }
    } catch (_e) { /* noop — never let telemetry crash the boundary */ }
  }
  render() {
    if (this.state.error) {
      return (
        <div
          className="min-h-screen flex flex-col items-center justify-center p-8 bg-red-50 text-center"
          data-testid="kiosk-crash-screen"
        >
          <AlertCircle className="w-20 h-20 text-red-500 mb-6" />
          <h1 className="font-serif text-3xl text-charcoal-900 mb-2">Something went wrong</h1>
          <p className="text-charcoal-800/60 mb-6 max-w-md">
            The booth software hit an unexpected error. Your payment (if any) is safe.
            Tap below to restart the session.
          </p>
          <button
            onClick={() => { this.setState({ error: null }); window.location.reload(); }}
            className="btn-kiosk"
            data-testid="crash-restart-btn"
          >
            Restart booth
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}


// Fixed 4-photo strip layout (2x6" strip).
// IMPORTANT: These dimensions MUST match backend `print_generator.py`
// `PHOTO_SLOTS` exactly, or the on-screen preview will show a different
// crop than what actually prints (WYSIWYG break — subject near horizontal
// edges disappear on paper). Backend prints at 500×325 with 50px x-padding
// inside each 600px-wide strip. If you change one, change both.
const FIXED_PHOTO_LAYOUT = {
  numPhotos: 4,
  printSize: '2x6',
  photoSlots: [
    { id: 1, x: 50, y: 50,   width: 500, height: 325 },
    { id: 2, x: 50, y: 425,  width: 500, height: 325 },
    { id: 3, x: 50, y: 800,  width: 500, height: 325 },
    { id: 4, x: 50, y: 1175, width: 500, height: 325 },
  ],
  brandingArea: { y: 1550, height: 220 },
  stripWidth: 600,
  stripHeight: 1800,
};

// Compute a legible foreground color for a given background hex. Uses WCAG
// relative luminance — if the venue picks a dark brand colour we return the
// cream palette, if they pick a light one we return the charcoal palette.
// Falls back safely for malformed hex.
function themeForBackground(bgHex) {
  const fallback = { bg: '#FDFCF8', text: '#1A1A1F', muted: 'rgba(26,26,31,0.65)', panel: '#FFFFFF', panelText: '#1A1A1F', border: 'rgba(26,26,31,0.10)' };
  if (!bgHex || typeof bgHex !== 'string') return fallback;
  const hex = bgHex.trim().replace('#', '');
  if (!/^([0-9a-fA-F]{6})$/.test(hex)) return { ...fallback, bg: bgHex };
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  // Perceived luminance — the ITU-R BT.601 formula (good enough for UI text).
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  const isDark = luminance < 0.55;  // threshold favours light text on ambiguous mid-tones
  return isDark
    ? {
        bg: bgHex,
        text: '#F5F1E8',                       // cream — high contrast on dark
        muted: 'rgba(245,241,232,0.72)',       // secondary text on dark
        panel: 'rgba(255,255,255,0.96)',       // white cards keep charcoal text inside
        panelText: '#1A1A1F',
        border: 'rgba(255,255,255,0.15)',
      }
    : {
        bg: bgHex,
        text: '#1A1A1F',                       // charcoal — high contrast on light
        muted: 'rgba(26,26,31,0.65)',
        panel: '#FFFFFF',
        panelText: '#1A1A1F',
        border: 'rgba(26,26,31,0.10)',
      };
}

const KioskApp = () => {
  const [searchParams] = useSearchParams();
  const { kioskId: routeKioskId } = useParams();
  
  const [screen, setScreen] = useState(SCREENS.PAIRING);
  const [config, setConfig] = useState(null);
  const [kioskId, setKioskId] = useState(routeKioskId || null);
  const [pairingCode, setPairingCode] = useState(searchParams.get('code') || '');
  const [pairingError, setPairingError] = useState('');
  
  // WebSocket state
  const [wsConnected, setWsConnected] = useState(false);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pairRetryTimeoutRef = useRef(null);  // separate from WS reconnect — pair-endpoint retries after transient failures
  const reconnectingTapCountRef = useRef({ count: 0, firstAt: 0 });  // 3-tap escape on Reconnecting screen
  
  // Payment state
  const [quantity, setQuantity] = useState(1);
  const [orderId, setOrderId] = useState(null);
  const [qrCode, setQrCode] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState('waiting');
  const [qrImageLoaded, setQrImageLoaded] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const creatingOrderRef = useRef(false);
  // Monotonic order-generation counter. Any in-flight create-order response
  // whose sequence is stale (a voucher order replaced it, or the session was
  // reset) is DISCARDED and its orphan order cancelled server-side. Without
  // this, a late base-order response overwrites the voucher order's orderId,
  // the poll-effect cleanup kills the pending capture transition, and the
  // customer is stranded on "Payment received" forever.
  const orderSeqRef = useRef(0);
  const [orderError, setOrderError] = useState(null);
  // Voucher entry (subtle "Have a code?" on payment screen)
  const [voucherOpen, setVoucherOpen] = useState(false);
  const [voucherInput, setVoucherInput] = useState('');
  const [voucherApplying, setVoucherApplying] = useState(false);
  const [voucherError, setVoucherError] = useState(null);
  const [appliedVoucher, setAppliedVoucher] = useState(null); // {code, discount, finalAmount, isFree}
  const [paymentTimer, setPaymentTimer] = useState(90); // 90s: 75s QR + 15s verify
  const [pollFailures, setPollFailures] = useState(0); // consecutive poll failures (Item #8 P1 hardening)
  
  // Photo capture state
  const [photos, setPhotos] = useState([]);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [countdown, setCountdown] = useState(null);
  const [showFlash, setShowFlash] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);  // NEW: Current captured photo for review
  const [retakesLeft, setRetakesLeft] = useState(1);  // NEW: Retakes remaining for current photo
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  // In-flight lock — prevents concurrent getUserMedia calls from the payment-
  // poll effect and the PHOTO_CAPTURE screen useEffect. Without this, both
  // fire before streamRef.current is set, leaking a second GPU-backed video
  // pipeline every session (a real contributor to native-crash on low-spec
  // kiosk hardware).
  const isStartingCameraRef = useRef(false);
  // One-shot guard for the WebRTC→ffmpeg auto-failover so a failed native
  // attempt can never ping-pong back into startCamera forever. Reset on any
  // successful WebRTC stream and on each auto-recovery probe.
  const nativeFallbackTriedRef = useRef(false);
  // Last-known camera health, reported in every WS heartbeat so the fleet
  // dashboard shows live camera status per booth.
  const cameraStatusRef = useRef('unknown');
  // Idle "living mirror" — low-res stream + motion detection state
  const idleVideoRef = useRef(null);
  const idleStreamRef = useRef(null);
  const idlePrevFrameRef = useRef(null);
  const idleLastMotionRef = useRef(0);
  const idleWatchdogRef = useRef({ lastTime: -1, stale: 0 });
  const [mirrorActive, setMirrorActive] = useState(false);
  // Auto-countdown flag — set to true right before `setScreen(PHOTO_CAPTURE)`
  // in `handleKeepPhoto` for photos 2-4, so the mount-time effect can kick
  // off the 3-2-1 countdown automatically instead of stranding the user on
  // a silent camera view (previously reported as "countdown never starts
  // after Keep & Next").
  const autoStartCountdownRef = useRef(false);

  // === v3.12.0 Native Camera Helper (ffmpeg bypasses Chromium WebRTC) ===
  // When cameraMode==='native' (set automatically after a camera-lifecycle
  // crash, or via Ctrl+Shift+N), frames come from ffmpeg over IPC into an
  // <img> — getUserMedia/<video> never run, so the Intel GPU driver crash
  // path is unreachable.
  const [nativeCameraMode, setNativeCameraMode] = useState(false);
  const nativeCameraModeRef = useRef(false);
  const nativeImgRef = useRef(null);
  const nativeLatestFrameRef = useRef(null); // Uint8Array of freshest JPEG
  const nativeFrameUrlRef = useRef(null);    // blob: URL currently displayed
  const nativeFeedActiveRef = useRef(false); // frame listener subscribed
  const appVersionRef = useRef(null);        // Electron shell version for heartbeat
  const mediaReadingRef = useRef(null);      // printer media counter for heartbeat
  const exitTapCountRef = useRef(0);         // hidden exit hotspot tap counter
  const exitTapTimerRef = useRef(null);
  // v3.12.0 pixel-exact print preview (composed strip shown before Print)
  const [stripPreview, setStripPreview] = useState(null);
  
  // Complete screen state - NEW
  const [downloadQrCode, setDownloadQrCode] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [helpQr, setHelpQr] = useState(null);
  const [printStages, setPrintStages] = useState(null); // {copies, perCopy, startedAt} — multi-copy wait tracker
  const [, setPrintTick] = useState(0); // 1s re-render tick while staged printing
  const [completeTimer, setCompleteTimer] = useState(30);
  
  // Error handling state - NEW
  const [currentError, setCurrentError] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);
  // Rolling buffer of all errors hit during the session — surfaced in the
  // bottom-right "Operator Error Console" so venue staff can debug on-site
  // without needing access to the admin dashboard.
  const { errors: bufferedErrors, pushError: pushBufferedError, clearErrors: clearBufferedErrors } = useErrorBuffer(50);
  const [timeoutRemaining, setTimeoutRemaining] = useState(60);
  const [cameraRetryCountdown, setCameraRetryCountdown] = useState(null);
  const [graceTimer, setGraceTimer] = useState(null); // 60s silent late-payment grace after visible timer
  const [lateResume, setLateResume] = useState(null); // {order_id, quantity, amount, expires_at}
  const networkMonitorRef = useRef(null);
  const sessionTimeoutRef = useRef(null);
  
  // Initialize network monitor
  useEffect(() => {
    networkMonitorRef.current = new NetworkMonitor((online) => {
      setIsOnline(online);
      if (!online && screen !== SCREENS.PAIRING && screen !== SCREENS.IDLE) {
        // Show network error but don't interrupt critical flows
        console.warn('Network went offline');
      }
    });
    
    return () => {
      networkMonitorRef.current?.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Log camera plug/unplug events with exact timestamps — THE missing signal
  // for diagnosing field "camera not connected" incidents (correlates with
  // system suspend/resume and print-burst power draw).
  useEffect(() => {
    const md = navigator.mediaDevices;
    if (!md || !md.addEventListener) return undefined;
    const onChange = async () => {
      try {
        const devices = await md.enumerateDevices();
        const cams = devices.filter((d) => d.kind === 'videoinput');
        boothLogger.info('camera', 'device_change', `USB device change — ${cams.length} camera(s) visible`, {
          count: cams.length,
          cameras: cams.map((c) => c.label || (c.deviceId || '').slice(-8) || 'unknown'),
        });
      } catch (_e) { /* noop */ }
    };
    md.addEventListener('devicechange', onChange);
    return () => md.removeEventListener('devicechange', onChange);
  }, []);

  // LIVING WELCOME SCREEN — low-res camera runs while idle; sustained motion
  // fades in a full-screen live mirror; 30s of stillness fades it out.
  // Side benefit: a continuously-open camera never gets USB-suspended by
  // Windows (prime suspect for field "camera not connected" incidents), and
  // a dead camera is caught at idle — staff get alerted BEFORE anyone pays.
  useEffect(() => {
    if (screen !== SCREENS.IDLE || nativeCameraModeRef.current) {
      setMirrorActive(false);
      return undefined;
    }
    let cancelled = false;
    let failLogged = false;
    let idleFailCount = 0;
    let replugTriedThisEpisode = false;
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 36;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });

    const stopStream = () => {
      idleStreamRef.current?.getTracks().forEach((t) => t.stop());
      idleStreamRef.current = null;
    };

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 424 }, height: { ideal: 240 }, frameRate: { ideal: 15, max: 15 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        idleStreamRef.current = stream;
        if (idleVideoRef.current) {
          idleVideoRef.current.srcObject = stream;
          idleVideoRef.current.play().catch(() => {});
        }
        cameraStatusRef.current = 'ok';
        if (idleFailCount > 0) {
          boothLogger.info('camera', 'idle_auto_recovered', `Idle camera back after ${idleFailCount} failed attempt(s)`);
        }
        idleFailCount = 0;
        replugTriedThisEpisode = false;
        failLogged = false;
        idlePrevFrameRef.current = null;
        idleWatchdogRef.current = { lastTime: -1, stale: 0 };
      } catch (err) {
        if (cancelled) return;
        idleFailCount += 1;
        if (!failLogged) {
          failLogged = true;
          cameraStatusRef.current = 'failed';
          // Same event shape as session failures so the staff email fires —
          // the point is catching a dead camera BEFORE a customer pays.
          boothLogger.error('camera', 'setup_failed', `Idle mirror getUserMedia failed: ${err?.message || err}`, {
            name: err?.name, phase: 'idle',
          });
        }
        // Idle-state USB replug (gap found Oct 2026: OASIS sat camera-dead for
        // 80+ min at idle with no replug — the replug was only wired to
        // in-session error screens). One software replug per outage episode,
        // after the 2nd consecutive failure (~2 min in).
        if (idleFailCount >= 2 && !replugTriedThisEpisode && window.piczooKiosk?.cameraUsbReplug) {
          replugTriedThisEpisode = true;
          boothLogger.warn('camera', 'usb_replug_attempt', 'Idle camera missing ~2 min — attempting software USB replug');
          window.piczooKiosk.cameraUsbReplug().then((r) => {
            boothLogger.info('camera', 'usb_replug_result', `USB replug ${r?.success ? 'SUCCEEDED' : 'failed'}`, r);
          }).catch(() => {});
        }
        setTimeout(() => { if (!cancelled) start(); }, 60000);
      }
    };
    start();

    const detect = setInterval(() => {
      const v = idleVideoRef.current;
      if (!v || !idleStreamRef.current || v.readyState < 2) return;
      try {
        ctx.drawImage(v, 0, 0, 64, 36);
        const frame = ctx.getImageData(0, 0, 64, 36).data;
        const prev = idlePrevFrameRef.current;
        idlePrevFrameRef.current = frame;
        if (!prev) return;
        let moved = 0;
        let samples = 0;
        for (let i = 0; i < frame.length; i += 16) {
          samples += 1;
          const d = Math.abs(frame[i] - prev[i]) + Math.abs(frame[i + 1] - prev[i + 1]) + Math.abs(frame[i + 2] - prev[i + 2]);
          if (d > 60) moved += 1;
        }
        if (moved / samples > 0.05) {
          idleLastMotionRef.current = Date.now();
          setMirrorActive(true);
        } else if (Date.now() - idleLastMotionRef.current > 30000) {
          setMirrorActive(false);
        }
      } catch (_e) { /* skip frame */ }
    }, 500);

    // Frame watchdog — long-running Chromium streams can silently freeze
    // after days of uptime. Frozen currentTime for 30s → restart the stream.
    const watchdog = setInterval(() => {
      const v = idleVideoRef.current;
      if (!v || !idleStreamRef.current) return;
      const wd = idleWatchdogRef.current;
      if (v.currentTime === wd.lastTime) {
        wd.stale += 1;
        if (wd.stale >= 3) {
          boothLogger.warn('camera', 'idle_stream_stalled', 'Idle mirror frames frozen — restarting stream');
          wd.stale = 0;
          stopStream();
          start();
        }
      } else {
        wd.stale = 0;
        wd.lastTime = v.currentTime;
      }
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(detect);
      clearInterval(watchdog);
      stopStream();
      setMirrorActive(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Catch ALL unhandled errors and promise rejections — anything not caught
  // by component-level try/catch ends up here and gets surfaced in the
  // operator console. Without this, JS errors die silently in the renderer.
  useEffect(() => {
    const onUnhandledRejection = (e) => {
      const reason = e.reason || {};
      pushBufferedError({
        level: 'error',
        code: 'UNHANDLED_REJECTION',
        title: 'Unhandled promise rejection',
        message: reason.message || String(reason).slice(0, 200),
        technicalDetails: reason.stack || '',
      });
    };
    const onWindowError = (e) => {
      pushBufferedError({
        level: 'error',
        code: 'JS_ERROR',
        title: e.message || 'Script error',
        message: `${e.filename}:${e.lineno}:${e.colno}`,
        technicalDetails: e.error?.stack || '',
      });
    };
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onWindowError);
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onWindowError);
    };
  }, [pushBufferedError]);
  
  // Session timeout — mid-session abandonment protection ONLY.
  // v3.14.0 (user-approved): the IDLE screen has NO timer at all — the
  // booth rests on "Touch to Start" indefinitely, no popup ever, until
  // someone touches it. In-progress sessions (quantity/photos) reset
  // after 2 minutes of no interaction so a walk-away customer never
  // leaves the booth stuck for the next person.
  useEffect(() => {
    if (screen === SCREENS.QUANTITY_SELECT ||
        screen === SCREENS.PHOTO_CAPTURE || screen === SCREENS.PHOTO_REVIEW) {
      sessionTimeoutRef.current = new SessionTimeoutManager({
        timeout: 2 * 60 * 1000, // 2 minutes
        warningTime: 30 * 1000, // 30 second warning
        onWarning: (seconds) => {
          setShowTimeoutWarning(true);
          setTimeoutRemaining(seconds);
        },
        onTimeout: () => {
          setShowTimeoutWarning(false);
          handleReset();
        }
      });
    }
    
    return () => {
      sessionTimeoutRef.current?.destroy();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);
  
  // Handle session continue
  const handleSessionContinue = () => {
    setShowTimeoutWarning(false);
    sessionTimeoutRef.current?.resetTimer();
  };
  
  // Handle errors with recovery options
  const handleError = useCallback((error, context = '') => {
    const parsedError = parseError(error);
    console.error(`Error in ${context}:`, parsedError);

    // Build a structured payload that captures EVERYTHING useful for debugging
    const technicalDetails = (() => {
      try {
        const parts = [];
        if (error?.stack) parts.push(error.stack);
        else if (error?.message) parts.push(error.message);
        if (error?.response) {
          parts.push(`HTTP ${error.response.status}`);
          if (error.response.data) parts.push(JSON.stringify(error.response.data));
        }
        return parts.join('\n');
      } catch (_) { return ''; }
    })();

    setCurrentError({
      ...parsedError,
      context,
      technicalDetails,
      timestamp: Date.now(),
    });

    // Always log to operator console so staff can see history
    pushBufferedError({
      level: parsedError.recoverable ? 'warn' : 'error',
      code: parsedError.code,
      title: parsedError.title,
      message: parsedError.message,
      data: { context, originalError: error?.message, response: error?.response?.data },
      technicalDetails,
    });

    // Don't switch to error screen for minor issues
    if (!parsedError.recoverable) {
      setScreen(SCREENS.ERROR);
    }
  }, [pushBufferedError]);
  
  // Clear error and retry
  const handleErrorRetry = useCallback(() => {
    const errorContext = currentError?.context;
    setCurrentError(null);
    
    // Return to appropriate screen based on context
    switch (errorContext) {
      case 'camera':
        setScreen(SCREENS.PHOTO_CAPTURE);
        break;
      case 'print':
        setScreen(SCREENS.PREVIEW);
        break;
      case 'payment':
        setScreen(SCREENS.PAYMENT);
        break;
      default:
        setScreen(SCREENS.IDLE);
    }
  }, [currentError]);
  
  // Full reset
  // Guards against duplicate `paid` processing from backend cache +
  // webhook + poll firing within milliseconds of each other. Declared
  // here (before the useCallbacks that reference them) to keep the TDZ
  // rule of const-declarations happy for eager readers.
  const paidHandledRef = useRef(false);
  const paymentTransitionTimerRef = useRef(null);

  const handleReset = useCallback(() => {
    creatingOrderRef.current = false;
    orderSeqRef.current += 1;
    setConfirmCancel(false);
    setQrImageLoaded(false);
    setScreen(SCREENS.IDLE);
    setPhotos([]);
    setCurrentPhotoIndex(0);
    setQuantity(1);
    setOrderId(null);
    setQrCode(null);
    setPaymentStatus('waiting');
    setPaymentTimer(90);
    setCapturedPhoto(null);
    setRetakesLeft(1);
    setCurrentError(null);
    setShowTimeoutWarning(false);
    paidHandledRef.current = false;
    if (paymentTransitionTimerRef.current) {
      clearTimeout(paymentTransitionTimerRef.current);
      paymentTransitionTimerRef.current = null;
    }
    stopCamera();
  }, []);
  
  // Exit modal
  const [showExitModal, setShowExitModal] = useState(false);
  const [exitPassword, setExitPassword] = useState('');
  const [exitError, setExitError] = useState('');

  // Hidden exit gesture — 5 taps in the top-left 96px corner within 2s.
  // Window-level coordinate listener: nothing overlays the UI, so buttons
  // in that corner (e.g. the quantity screen's Back button) still work.
  useEffect(() => {
    const excluded = [SCREENS.PAIRING, SCREENS.RECONNECTING, SCREENS.DISCONNECTED, SCREENS.ERROR];
    if (excluded.includes(screen)) return undefined;
    const onTap = (e) => {
      if (e.clientX > 96 || e.clientY > 96) return;
      exitTapCountRef.current += 1;
      if (exitTapTimerRef.current) clearTimeout(exitTapTimerRef.current);
      if (exitTapCountRef.current >= 5) {
        exitTapCountRef.current = 0;
        setShowExitModal(true);
        return;
      }
      exitTapTimerRef.current = setTimeout(() => { exitTapCountRef.current = 0; }, 2000);
    };
    window.addEventListener('pointerdown', onTap);
    return () => window.removeEventListener('pointerdown', onTap);
  }, [screen]);

  // WebSocket connection for real-time updates
  // Live-screen ref — the reconnect chain below must gate on the CURRENT
  // screen, not the closure-captured one. The old closure read stale
  // `screen`/`kioskId` state (null/'pairing' at pair time), which
  // permanently killed retries after the first drop and left the badge
  // stuck on "Reconnecting…" forever even with perfect internet.
  const screenRef = useRef(SCREENS.PAIRING);
  useEffect(() => { screenRef.current = screen; }, [screen]);
  const wsAuthFailedRef = useRef(false);

  const connectWebSocket = useCallback((kId, code) => {
    // Guard BOTH open and still-connecting sockets — creating a second socket
    // while the first is mid-handshake causes duplicate connections whose
    // teardown flaps the "Reconnecting" badge and booth online status.
    const rs = wsRef.current?.readyState;
    if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return;
    
    // Include pairing code as auth token
    const token = code || localStorage.getItem('piczoo_pairing_code') || '';
    const ws = new WebSocket(`${WS_URL}/ws/kiosk/${kId}?token=${encodeURIComponent(token)}`);
    wsRef.current = ws;

    ws.onopen = () => {
      if (wsRef.current !== ws) return; // stale socket
      console.log('WebSocket connected');
      boothLogger.info('ws', 'open', 'WebSocket connected');
      wsAuthFailedRef.current = false;
      setWsConnected(true);
      ws.send(JSON.stringify({
        type: 'heartbeat',
        camera_mode: nativeCameraModeRef.current ? 'native' : 'standard',
        camera_status: cameraStatusRef.current,
        app_version: appVersionRef.current || undefined,
        media: mediaReadingRef.current || undefined,
      }));
    };
    
    ws.onmessage = (event) => {
      if (wsRef.current !== ws) return; // stale socket
      const message = JSON.parse(event.data);
      console.log('WebSocket message:', message);
      boothLogger.debug('ws', 'message', `Received ${message.type}`, { type: message.type });
      
      switch (message.type) {
        case 'config_update':
          setConfig(prev => ({ ...prev, ...message.data }));
          break;
        case 'pricing_update':
          setConfig(prev => ({ ...prev, pricing: message.data }));
          break;
        case 'force_disconnect':
          boothLogger.warn('ws', 'force_disconnect', 'Admin force-disconnected this booth', { reason: message.reason });
          setScreen(SCREENS.DISCONNECTED);
          localStorage.removeItem('piczoo_kiosk_id');
          localStorage.removeItem('piczoo_pairing_code');
          localStorage.removeItem('piczoo_has_paired_before');
          ws.close();
          break;
        case 'late_payment_resume':
          boothLogger.info('payment', 'late_resume_offer', 'Late payment arrived — resume offer received', message.data);
          setLateResume(message.data);
          break;
        case 'heartbeat_ack':
          break;
        case 'command':
          boothLogger.info('ws', 'command', `Remote command: ${message.command || 'unknown'}`, message);
          if (window.piczooKiosk?.dispatchCommand) {
            window.piczooKiosk.dispatchCommand(message).catch((err) => {
              console.error('dispatchCommand failed:', err);
              boothLogger.error('ws', 'command_failed', `dispatchCommand: ${err?.message || err}`, { command: message.command });
            });
          } else {
            console.warn('Received command outside Electron — ignoring', message);
            boothLogger.warn('ws', 'command_no_electron', 'Command received outside Electron shell', { command: message.command });
          }
          break;
        default:
          console.log('Unknown message type:', message.type);
      }
    };
    
    ws.onclose = (event) => {
      if (wsRef.current !== ws) return; // stale socket — a newer one is live
      console.log('WebSocket disconnected, code:', event.code, 'reason:', event.reason);
      boothLogger.warn('ws', 'close', `WebSocket closed (code=${event.code})`, { code: event.code, reason: event.reason });
      setWsConnected(false);
      
      // Don't reconnect if auth failed (4001/4003)
      if (event.code === 4001 || event.code === 4003) {
        console.error('WebSocket auth failed:', event.reason);
        boothLogger.error('ws', 'auth_failed', `WebSocket auth failed (${event.code})`, { reason: event.reason });
        wsAuthFailedRef.current = true;
        return;
      }
      
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = setTimeout(() => {
        reconnectTimeoutRef.current = null;
        const s = screenRef.current;
        if (s !== SCREENS.PAIRING && s !== SCREENS.DISCONNECTED) {
          connectWebSocket(kId, code);
        }
      }, 5000);
    };
    
    ws.onerror = (error) => {
      if (wsRef.current !== ws) return; // stale socket
      console.error('WebSocket error:', error);
      boothLogger.error('ws', 'error', 'WebSocket socket error', { message: String(error?.message || error) });
      setWsConnected(false);
    };
  }, []);

  // Watchdog: the instant the network comes back (or the screen changes)
  // while the socket is down, reconnect immediately instead of hoping a
  // pending 5s retry is still alive.
  useEffect(() => {
    if (!isOnline || !kioskId || wsAuthFailedRef.current) return;
    if (screen === SCREENS.PAIRING || screen === SCREENS.RECONNECTING || screen === SCREENS.DISCONNECTED) return;
    const rs = wsRef.current?.readyState;
    if (rs === WebSocket.OPEN || rs === WebSocket.CONNECTING) return;
    connectWebSocket(kioskId, localStorage.getItem('piczoo_pairing_code') || '');
  }, [isOnline, kioskId, screen, connectWebSocket]);

  // Heartbeat interval
  useEffect(() => {
    if (!wsConnected || !wsRef.current) return;
    
    const heartbeatInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: 'heartbeat',
          camera_mode: nativeCameraModeRef.current ? 'native' : 'standard',
          camera_status: cameraStatusRef.current,
          app_version: appVersionRef.current || undefined,
          media: mediaReadingRef.current || undefined,
        }));
      }
    }, 30000);
    
    return () => clearInterval(heartbeatInterval);
  }, [wsConnected]);

  // Poll the Electron main process for the printer media counter reading
  useEffect(() => {
    const poll = async () => {
      try {
        if (window.piczooKiosk?.getMediaReading) {
          const r = await window.piczooKiosk.getMediaReading();
          if (r) mediaReadingRef.current = { supported: !!r.supported, remaining: r.remaining, initial: r.initial ?? null, source: r.source, error: r.error || null };
        }
      } catch (_) { /* fail-silent */ }
    };
    poll();
    const t = setInterval(poll, 60000);
    return () => clearInterval(t);
  }, []);

  // Print Delivery Guarantee (v3.16.0): the Electron layer auto-reprints any
  // job interrupted by a restart/power loss. Surface it so staff know why a
  // print just came out of an idle booth.
  useEffect(() => {
    try {
      window.piczooKiosk?.onPendingPrintRecovered?.((data) => {
        try {
          toast.success(`Recovered an interrupted print (${data?.copies || 1} ${(data?.copies || 1) === 1 ? 'copy' : 'copies'}) — it is printing now.`, { duration: 15000 });
        } catch (_) {}
      });
    } catch (_) { /* fail-silent outside Electron */ }
  }, []);

  // Outage Mode: if order creation failed (server/DB outage), silently retry
  // every 30s so the booth self-recovers without staff intervention.
  useEffect(() => {
    if (!orderError || screen !== SCREENS.PAYMENT) return undefined;
    const t = setTimeout(() => {
      boothLogger.info('payment', 'order_auto_retry', 'Auto-retrying order creation after failure');
      createPaymentOrder(1);
    }, 30000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderError, screen]);

  // Tell the main process whether a customer session is active — the media
  // counter only queries the printer when the booth is truly idle.
  useEffect(() => {
    const inSession = ![SCREENS.PAIRING, SCREENS.RECONNECTING, SCREENS.IDLE, SCREENS.DISCONNECTED, SCREENS.ERROR].includes(screen);
    try { window.piczooKiosk?.setMediaSessionActive?.(inSession); } catch (_) { /* fail-silent */ }
  }, [screen]);

  // ---------- Booth activity logger wiring ----------
  // Keep the logger's identity in sync with React state so every log entry
  // is tagged with the right kiosk_id, current screen, and session_id.
  // This is what makes the admin "Activity Log" tab useful for post-mortem.
  useEffect(() => {
    boothLogger.configure({ kioskId, screen, sessionId: orderId || null });
  }, [kioskId, screen, orderId]);

  // Log every screen transition — the single most useful diagnostic signal.
  const prevScreenRef = useRef(SCREENS.PAIRING);
  useEffect(() => {
    if (prevScreenRef.current !== screen) {
      boothLogger.info('screen', 'transition', `${prevScreenRef.current} → ${screen}`, {
        from: prevScreenRef.current,
        to: screen,
        quantity,
        photoIndex: currentPhotoIndex,
      });
      prevScreenRef.current = screen;
    }
  }, [screen, quantity, currentPhotoIndex]);

  // ---------- Global diagnostic instrumentation ----------
  // These hooks close some of the biggest blind spots we've hit in
  // remote-debugging sessions:
  //   • `visibilitychange` catches Chromium throttling the whole tab
  //     when the kiosk window loses focus (Windows lock screen, another
  //     app steals focus, etc.). Timers get demoted to once-per-minute
  //     and eventually paused entirely — which looks EXACTLY like the
  //     "camera up, countdown never starts" symptom.
  //   • `error` and `unhandledrejection` catch anything the app throws
  //     that isn't caught by our per-feature try/catch blocks. Right now
  //     an unhandled promise rejection in the countdown chain would just
  //     stop the countdown silently.
  //   • `pageshow` / `pagehide` help distinguish "renderer froze" from
  //     "OS suspended the process".
  useEffect(() => {
    const onVisibility = () => {
      boothLogger.info('lifecycle', 'visibility', `Document visibility → ${document.visibilityState}`, {
        visibilityState: document.visibilityState,
        hidden: document.hidden,
        screen,
      });
    };
    const onError = (e) => {
      boothLogger.error('runtime', 'window_error', `Uncaught: ${e.message || 'unknown'}`, {
        message: e.message,
        filename: e.filename,
        lineno: e.lineno,
        colno: e.colno,
        stack: e.error?.stack,
        screen,
      });
    };
    const onRejection = (e) => {
      const reason = e.reason;
      boothLogger.error('runtime', 'unhandled_rejection', `Unhandled promise rejection: ${reason?.message || reason || 'unknown'}`, {
        message: reason?.message,
        name: reason?.name,
        stack: reason?.stack,
        screen,
      });
    };
    const onPageHide = () => boothLogger.warn('lifecycle', 'pagehide', 'Renderer page hidden (potential suspend)', { screen });
    const onPageShow = () => boothLogger.info('lifecycle', 'pageshow', 'Renderer page shown', { screen });
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('error', onError);
    window.addEventListener('unhandledrejection', onRejection);
    window.addEventListener('pagehide', onPageHide);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('error', onError);
      window.removeEventListener('unhandledrejection', onRejection);
      window.removeEventListener('pagehide', onPageHide);
      window.removeEventListener('pageshow', onPageShow);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup WebSocket + pair-retry timers on unmount
  useEffect(() => {
    return () => {
      if (wsRef.current) wsRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      if (pairRetryTimeoutRef.current) clearTimeout(pairRetryTimeoutRef.current);
      boothLogger.flush();
    };
  }, []);

  // Check for saved pairing on mount, OR auto-pair from URL ?code= param
  useEffect(() => {
    const urlCode = searchParams.get('code');
    const savedKioskId = localStorage.getItem('piczoo_kiosk_id');
    const savedCode = localStorage.getItem('piczoo_pairing_code');
    // STICKY-PAIRED FLAG — set only after a successful manual pair. Auto-
    // reconnect on startup is gated STRICTLY on this flag. Without it we
    // do nothing at all, so a fresh install (or a reinstall that wiped
    // AppData, or a backend force-disconnect that cleared this flag)
    // sees the pairing input on boot and never touches the API.
    const hasPairedBefore = localStorage.getItem('piczoo_has_paired_before') === '1';

    // If the URL carries a DIFFERENT code than what's saved, the operator is
    // intentionally re-pairing this booth (e.g. reopened kiosk-electron with
    // a fresh code from the admin wizard). Clear the stale localStorage so
    // the "previously paired, never unpair" recovery logic kicks in with
    // the RIGHT booth identity — not the ghost of an old pairing.
    if (urlCode && savedCode && urlCode !== savedCode) {
      console.warn('URL pairing code differs from saved — clearing stale localStorage before re-pair');
      localStorage.removeItem('piczoo_kiosk_id');
      localStorage.removeItem('piczoo_pairing_code');
      localStorage.removeItem('piczoo_has_paired_before');
    }

    // A URL ?code= param is an EXPLICIT operator action (they just launched
    // the app from the admin wizard with a fresh code) — always honour it.
    // A saved code from localStorage is only auto-used when the sticky
    // "has-paired-before" flag is set — i.e. this booth has genuinely
    // finished a pair in the past. Without that flag we treat every start
    // like a fresh install: pairing input only, zero API calls, zero
    // "Reconnecting" screen surprises before the operator has done
    // anything.
    const codeToUse = urlCode || (hasPairedBefore ? savedCode : null);

    if (codeToUse) {
      setPairingCode(codeToUse);
      handlePairingWithCode(codeToUse);

      // Recover any cached photos from a previous interrupted session
      const kid = savedKioskId;
      if (kid) {
        getCachedSession(kid).then(cached => {
          if (cached && cached.photos.filter(Boolean).length > 0) {
            console.log(`Recovered ${cached.photos.filter(Boolean).length} cached photos from interrupted session`);
            const validPhotos = cached.photos.filter(Boolean);
            setPhotos(validPhotos);
            if (validPhotos.length >= FIXED_PHOTO_LAYOUT.numPhotos) {
              setScreen(SCREENS.PREVIEW);
            } else {
              setCurrentPhotoIndex(validPhotos.length);
              setScreen(SCREENS.PHOTO_CAPTURE);
              startCamera();
            }
          }
        }).catch(() => { /* cache is best-effort */ });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Payment timer
  useEffect(() => {
    if (screen === SCREENS.PAYMENT && paymentTimer > 0) {
      const timer = setInterval(() => setPaymentTimer(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (paymentTimer === 0 && screen === SCREENS.PAYMENT && graceTimer === null) {
      // SILENT GRACE: don't kill the order yet — UPI can land a little late.
      // Keep polling quietly for 60 more seconds before giving up.
      boothLogger.info('payment', 'grace_started', 'Visible window over — 60s silent grace, still polling');
      setGraceTimer(60);
    }
  }, [screen, paymentTimer, graceTimer]);

  // Grace countdown — expiry does what the hard timeout used to do.
  useEffect(() => {
    if (screen !== SCREENS.PAYMENT || graceTimer === null) return undefined;
    if (graceTimer > 0) {
      const t = setInterval(() => setGraceTimer((g) => (g === null ? null : g - 1)), 1000);
      return () => clearInterval(t);
    }
    // Grace over: close QR at Razorpay, release voucher, back to start.
    if (orderId) {
      axios.post(`${API}/payments/${orderId}/cancel`).catch(() => {});
    }
    boothLogger.info('payment', 'grace_expired', 'Grace window over without payment — resetting');
    setGraceTimer(null);
    setAppliedVoucher(null);
    setVoucherInput('');
    setVoucherError(null);
    handleReset();
    return undefined;
  }, [screen, graceTimer, orderId, handleReset]);

  // Any screen change away from PAYMENT clears grace state.
  useEffect(() => {
    if (screen !== SCREENS.PAYMENT && graceTimer !== null) setGraceTimer(null);
  }, [screen, graceTimer]);

  // Late-resume offer expires automatically.
  useEffect(() => {
    if (!lateResume?.expires_at) return undefined;
    const ms = new Date(lateResume.expires_at).getTime() - Date.now();
    if (ms <= 0) { setLateResume(null); return undefined; }
    const t = setTimeout(() => setLateResume(null), ms);
    return () => clearTimeout(t);
  }, [lateResume]);

  const claimLateResume = async (e) => {
    e.stopPropagation();
    const lr = lateResume;
    if (!lr) return;
    try {
      const r = await axios.post(`${API}/payments/${lr.order_id}/consume-late-resume?kiosk_id=${kioskId}`);
      setLateResume(null);
      orderSeqRef.current += 1;
      setQuantity(r.data.quantity || 1);
      setOrderId(lr.order_id);
      paidHandledRef.current = true;
      setPaymentStatus('success');
      boothLogger.info('payment', 'late_resume_claimed', 'Late-paid session resumed on booth', { order_id: lr.order_id });
      setScreen(SCREENS.PHOTO_CAPTURE);
    } catch (err) {
      boothLogger.warn('payment', 'late_resume_failed', `Resume claim failed: ${err.response?.data?.detail || err.message}`, { order_id: lr.order_id });
      setLateResume(null);
    }
  };

  // Poll payment status. After 3 consecutive failures we surface a
  // "connectivity issue — your payment is safe" banner without resetting the
  // order or timer, so a brief Wi-Fi blip doesn't strand a paid customer.
  useEffect(() => {
    if (screen === SCREENS.PAYMENT && orderId) {
      paidHandledRef.current = false;
      let pollTick = 0;
      const pollInterval = setInterval(async () => {
        pollTick += 1;
        try {
          const response = await axios.get(`${API}/payments/${orderId}/status`);
          // Reset failure counter on any successful response
          setPollFailures(0);
          // Log every poll response so we can trace exactly what the
          // backend was returning at the moment the customer perceived a
          // hang (was it stuck at 'created'? did it flap between states?).
          // debug level keeps the log manageable if a session drags out.
          boothLogger.debug('payment', 'poll_response', `Poll #${pollTick} → ${response.data.status}`, {
            tick: pollTick,
            status: response.data.status,
            amount: response.data.amount,
          });
          if (response.data.status === 'paid' && !paidHandledRef.current) {
            paidHandledRef.current = true;
            boothLogger.info('payment', 'paid', `Order ${orderId} marked paid — proceeding to capture`, {
              order_id: orderId,
              amount: response.data.amount,
            });
            setPaymentStatus('success');
            clearInterval(pollInterval);
            // CRITICAL FIX (A1): DO NOT call startCamera() here. Previously
            // we called both setScreen(PHOTO_CAPTURE) and startCamera() in
            // the same synchronous tick — startCamera fired BEFORE React
            // committed the screen change, so `videoRef.current` was null
            // and the stream attached to a stale/wrong element. Leaving
            // the mount-effect on [screen] as the SOLE driver of the
            // camera guarantees correct ordering: video mounts first,
            // effect runs, camera starts, stream attaches to the correct
            // video element. This is exactly how it worked pre-paywall.
            //
            // Timer is stored in a ref so navigating away (timeout reset,
            // rollback, escape hatch) can cancel the pending transition
            // and we never bounce to capture from a screen the user has
            // already left.
            paymentTransitionTimerRef.current = setTimeout(() => {
              paymentTransitionTimerRef.current = null;
              boothLogger.info('payment', 'transition_fire', 'Firing screen transition to photo_capture', {
                order_id: orderId,
              });
              setScreen(SCREENS.PHOTO_CAPTURE);
            }, 2000);
          }
        } catch (err) {
          console.error('Payment poll error:', err);
          boothLogger.warn('payment', 'poll_error', `Poll failed: ${err?.message || err}`, {
            status: err?.response?.status,
            consecutive_failures: pollFailures + 1,
          });
          setPollFailures(prev => prev + 1);
        }
      }, 3000);
      return () => {
        clearInterval(pollInterval);
        // Cancel any pending transition — prevents a "paid" flash mid-
        // session-timeout from stranding the user on capture.
        if (paymentTransitionTimerRef.current) {
          clearTimeout(paymentTransitionTimerRef.current);
          paymentTransitionTimerRef.current = null;
        }
      };
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, orderId]);

  // Complete screen timer with QR code
  useEffect(() => {
    if (screen === SCREENS.COMPLETE && completeTimer > 0) {
      const timer = setInterval(() => setCompleteTimer(t => t - 1), 1000);
      return () => clearInterval(timer);
    } else if (completeTimer === 0 && screen === SCREENS.COMPLETE) {
      resetKiosk();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, completeTimer]);

  // Cleanup camera on unmount
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // v3.12.0 — Ask the Electron main process which camera mode this booth
  // uses. 'native' means ffmpeg capture (post-crash failover or manual
  // Ctrl+Shift+N toggle). Browsers / older shells fall through to WebRTC.
  useEffect(() => {
    const api = window.piczooKiosk;
    if (!api?.getCameraMode) return;
    api.getCameraMode().then((res) => {
      const useNative = !!(res?.available && res?.cameraMode === 'native');
      nativeCameraModeRef.current = useNative;
      setNativeCameraMode(useNative);
      if (useNative) {
        boothLogger.info('camera', 'native_mode_enabled', 'Native ffmpeg camera helper active — Chromium WebRTC bypassed', res);
      }
    }).catch(() => {});
  }, []);

  // v3.14.0 — Compose the pixel-exact strip when the preview screen opens
  // so the customer sees EXACTLY what the printer will output. Runs across
  // PREVIEW → PRINTING since the auto-print transition happens instantly.
  useEffect(() => {
    if ((screen === SCREENS.PREVIEW || screen === SCREENS.PRINTING) && photos.length > 0 && !stripPreview) {
      let cancelled = false;
      setStripPreview(null);
      generateStripImage().then((dataUrl) => {
        if (cancelled) return;
        setStripPreview(dataUrl);
        boothLogger.info('preview', 'exact_strip_rendered', 'Pixel-exact print preview composed', {
          size_kb: Math.round(dataUrl.length / 1024),
          photos: photos.length,
        });
      }).catch((e) => {
        boothLogger.warn('preview', 'exact_strip_failed', `Strip compose failed — CSS approximation shown: ${e?.message || e}`);
      });
      return () => { cancelled = true; };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // v3.14.0 (user-approved) — AUTO-PRINT: entering the preview instantly
  // starts the print job. No Preview/Print buttons; PREVIEW exists only as
  // a stepping stone so handlePrint reads the fresh 4-photo state.
  useEffect(() => {
    if (screen === SCREENS.PREVIEW) {
      const t = setTimeout(() => handlePrint(), 400);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // Re-attach camera stream when entering PHOTO_CAPTURE screen. NOTE: the
  // payment poll handler also calls startCamera() inline right before setting
  // this screen — but startCamera() is now idempotent (see the guard at the
  // top of that function) so a duplicate call is a no-op cheap reuse instead
  // of a duplicate getUserMedia (which was leaking a second stream every
  // session).
  useEffect(() => {
    if (screen === SCREENS.PHOTO_CAPTURE) {
      // v3.10.1 — Re-enable any tracks we disabled on photo_review (the
      // orphan-stream-segfault mitigation). Symmetric to the disable
      // block below. Idempotent — no-op if tracks are already enabled.
      try {
        if (streamRef.current) {
          for (const track of streamRef.current.getVideoTracks()) {
            if (!track.enabled) {
              track.enabled = true;
              boothLogger.debug('camera', 'track_reenabled', 'Re-enabled video track for next photo', {
                label: track.label,
                readyState: track.readyState,
              });
            }
          }
        }
      } catch (_e) { /* noop */ }

      // If stream is already live, re-attach + resume playback
      if (streamRef.current && videoRef.current) {
        if (videoRef.current.srcObject !== streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
        }
        // Only call play() when actually paused — otherwise Chromium
        // throws a benign but noisy "play() aborted by reload" AbortError
        // every time the effect re-runs (cluttered activity logs).
        if (videoRef.current.paused) {
          try { videoRef.current.play().catch(() => {}); } catch (_e) { /* noop */ }
        }
      } else if (!streamRef.current) {
        // No stream yet — start camera (idempotent, safe to call anytime)
        startCamera();
      }

      // AUTO-COUNTDOWN: previously we only auto-counted for photos 2-4
      // (relying on `autoStartCountdownRef` set by handleKeepPhoto).
      // Photo 1 was left as a manual tap, which felt like a hang to real
      // users — they'd pay, see the camera come up, and then wait for
      // something to happen. Now we auto-count for ALL photos.
      //
      // Poll for the stream to be ready (getUserMedia can take up to 3s
      // on first grant per the production activity logs). Once ready,
      // wait an extra 500ms so the customer sees the live preview and
      // knows where to stand, then start the 3-2-1.
      let pollTimer = null;
      let kickoffTimer = null;
      let cancelled = false;

      const waitForStreamThenCountdown = (attemptsLeft) => {
        if (cancelled) return;

        const streamReady = nativeCameraModeRef.current
          ? !!nativeLatestFrameRef.current
          : !!(streamRef.current && streamRef.current.active);
        const videoReady = nativeCameraModeRef.current
          ? !!(nativeImgRef.current && nativeImgRef.current.naturalWidth > 0)
          : !!(videoRef.current && videoRef.current.videoWidth > 0);
        if (streamReady && videoReady) {
          // Stream + first frame are up. Give the user a beat to see the
          // preview, then start the countdown.
          kickoffTimer = setTimeout(() => {
            if (cancelled) return;
            // FUNCTIONAL SETTER — reads latest state so we don't clobber a
            // manual button tap or a countdown that's already running.
            // The previous version closed over stale `countdown = null`
            // from the effect's initial render and could reset an
            // in-flight countdown back to 3.
            setCountdown((prev) => {
              if (prev !== null) return prev;
              boothLogger.info('capture', 'auto_countdown', `Auto-starting countdown for photo #${photos.length + 1}`, {
                photo_index: photos.length,
                trigger: autoStartCountdownRef.current ? 'return_from_review' : 'stream_ready',
              });
              autoStartCountdownRef.current = false;
              return 3;
            });
          }, 500);
          return;
        }

        // Not ready yet — poll again. 40 attempts × 200ms = 8s max wait.
        if (attemptsLeft <= 0) {
          boothLogger.warn('capture', 'auto_countdown_timeout', 'Gave up waiting for camera stream after 8s', {
            streamReady, videoReady,
          });
          return;
        }
        pollTimer = setTimeout(() => waitForStreamThenCountdown(attemptsLeft - 1), 200);
      };

      // Kick the poll off after a tiny delay so the ref callback + play()
      // above have run.
      pollTimer = setTimeout(() => waitForStreamThenCountdown(40), 100);

      return () => {
        cancelled = true;
        if (pollTimer) clearTimeout(pollTimer);
        if (kickoffTimer) clearTimeout(kickoffTimer);
      };
    } else if (screen === SCREENS.PHOTO_REVIEW) {
      // Pause the video element while the review screen is up. Releases the
      // decode pipeline between captures — significant memory savings on
      // cheap kiosk hardware. Stream stays alive so we resume instantly.
      try {
        if (videoRef.current) videoRef.current.pause();
      } catch (_e) { /* noop */ }

      // v3.10.1 — CRITICAL CRASH FIX for Intel HD 530 / low-end Windows GPUs.
      // The TEST-booth 12:04:23 diagnostic bundle showed exitCode
      // 0xC0000005 (Windows access violation) firing 2 seconds after the
      // <video> element unmounted on photo_review, while the MediaStream
      // was still active. Chromium's WebRTC pipeline was trying to release
      // a video-decoder texture that the compositor had already freed —
      // classic use-after-free in the GPU path.
      //
      // Fix: disable (not stop) each video track when we enter photo_review.
      // Disabled tracks let Chromium safely release the decoder pipeline
      // without orphaning it. On return to photo_capture we re-enable — the
      // stream + camera stay connected so we don't pay the getUserMedia
      // cold-start cost again.
      try {
        if (streamRef.current) {
          for (const track of streamRef.current.getVideoTracks()) {
            if (track.enabled) {
              track.enabled = false;
              boothLogger.debug('camera', 'track_disabled', 'Disabled video track on photo_review — prevents orphan-stream segfault', {
                label: track.label,
                readyState: track.readyState,
              });
            }
          }
        }
      } catch (_e) { /* noop */ }

      // Memory diagnostic per photo — see how close to OOM we are.
      try {
        const mem = performance?.memory;
        if (mem) {
          boothLogger.debug('lifecycle', 'memory_snapshot', 'JS heap usage between captures', {
            used_MB: Math.round(mem.usedJSHeapSize / 1024 / 1024),
            total_MB: Math.round(mem.totalJSHeapSize / 1024 / 1024),
            limit_MB: Math.round(mem.jsHeapSizeLimit / 1024 / 1024),
            photos_captured: photos.length,
          });
        }
      } catch (_e) { /* noop */ }
    }
    // Deliberately not depending on `photos` / `capturedPhoto` / `countdown`
    // — the state values are read inside the poll closure at check-time,
    // so we get fresh reads without re-running the effect (which would
    // cancel the poll timer mid-flight after every capture).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  // ---------- v3.10.0 Comprehensive diagnostic instrumentation ----------
  // Everything set up here closes a specific blind spot we identified in
  // the GLADIA post-mortem. Runs once on mount; teardown cancels the
  // watchdog interval so it doesn't leak across hot-reloads in dev.
  useEffect(() => {
    // Layer 1 — Heartbeat to main process every 500ms. If the render
    // thread hangs (e.g. inside drawImage), the last heartbeat's
    // timestamp is preserved in main-process logs so we know EXACTLY
    // when execution froze — the biggest missing signal from GLADIA.
    let hbTimer = null;
    if (typeof window !== 'undefined' && window.piczooKiosk?.logHeartbeat) {
      hbTimer = setInterval(() => {
        try {
          window.piczooKiosk.logHeartbeat({
            screen,
            countdown,
            photos_captured: photos.length,
            has_stream: !!streamRef.current,
            has_video: !!videoRef.current,
            captured_photo: !!capturedPhoto,
            order_id: orderId || null,
            ts: Date.now(),
          });
        } catch (_e) { /* silent */ }
      }, 500);
    }

    // Layer 4 — Global input event capture in the CAPTURE phase, before
    // any framer-motion or React handler can intercept. If the operator
    // says "I tapped the button" and this log doesn't fire, the touch
    // never reached the DOM at all → hardware issue.
    const onPointerDown = (e) => {
      const target = e.target;
      const testId = target?.dataset?.testid || target?.closest?.('[data-testid]')?.dataset?.testid;
      boothLogger.debug('input', 'pointerdown_global', `pointerdown on ${target?.tagName || '?'}${testId ? ` [${testId}]` : ''}`, {
        tag: target?.tagName,
        testId,
        x: e.clientX,
        y: e.clientY,
        pointerType: e.pointerType,
      });
    };
    document.addEventListener('pointerdown', onPointerDown, true);

    // Layer 5 — PerformanceObserver for long tasks. Anything > 50ms of
    // main-thread blocking is worth knowing about (paints get skipped,
    // input events get delayed, the renderer looks "stuck" from the
    // user's perspective). Chromium/Electron only.
    let po = null;
    if (typeof PerformanceObserver !== 'undefined') {
      try {
        po = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration > 100) {  // filter noise, only real jank
              boothLogger.warn('perf', 'longtask', `Main thread blocked ${Math.round(entry.duration)}ms`, {
                duration_ms: Math.round(entry.duration),
                start_time: Math.round(entry.startTime),
                screen,
              });
            }
          }
        });
        po.observe({ entryTypes: ['longtask'] });
      } catch (_e) { /* longtask not supported on this build */ }
    }

    // Layer 4b — Intercept console.error / console.warn globally. Third-
    // party libraries (framer-motion, axios, WebRTC internals) log to
    // console but not through boothLogger — this catches them so the
    // combined log has EVERYTHING.
    const origError = console.error;
    const origWarn = console.warn;
    console.error = function (...args) {
      try {
        // Skip if boothLogger already routed here — avoid infinite loop
        const first = String(args[0] || '');
        if (!first.startsWith('[booth]')) {
          boothLogger.error('console', 'error', first.slice(0, 200), {
            args: args.slice(1, 5).map(a => {
              try { return String(a).slice(0, 200); } catch { return '?'; }
            }),
          });
        }
      } catch (_e) { /* silent */ }
      origError.apply(console, args);
    };
    console.warn = function (...args) {
      try {
        const first = String(args[0] || '');
        if (!first.startsWith('[booth]')) {
          boothLogger.warn('console', 'warn', first.slice(0, 200));
        }
      } catch (_e) { /* silent */ }
      origWarn.apply(console, args);
    };

    return () => {
      if (hbTimer) clearInterval(hbTimer);
      document.removeEventListener('pointerdown', onPointerDown, true);
      if (po) { try { po.disconnect(); } catch (_e) { /* noop */ } }
      console.error = origError;
      console.warn = origWarn;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, countdown, photos.length, capturedPhoto, orderId]);

  // Layer 3 recovery — on first mount, ship the previous session's local
  // log tail to backend. Closes the "renderer crashed before upload"
  // gap: even a hard crash's final events land in the admin panel on
  // the next boot.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.piczooKiosk?.getLastSessionTail) return;
    (async () => {
      try {
        const res = await window.piczooKiosk.getLastSessionTail();
        if (res?.ok && Array.isArray(res.events) && res.events.length) {
          boothLogger.info('lifecycle', 'previous_session_recovered', `Shipping ${res.events.length} events from previous session's local log`, {
            count: res.events.length,
          });
          // Re-inject each into the current buffer so it gets uploaded
          // normally with the current session's boothLogger config.
          for (const evt of res.events.slice(-200)) {
            try {
              boothLogger.log(evt.level || 'info', evt.category || 'recovered', evt.event || 'unknown',
                `[recovered] ${evt.message || ''}`, evt.data || null);
            } catch (_e) { /* silent */ }
          }
        }
      } catch (_e) { /* ignore */ }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- END v3.10.0 instrumentation ----------

  const handlePairingWithCode = async (code) => {
    setPairingError('');
    // Was this booth previously paired? We check TWO signals so a momentary
    // race where localStorage.piczoo_kiosk_id is lost (e.g. between an
    // Electron renderer crash and the auto-reload, or an unrelated hydration
    // race) doesn't cause us to fall through to the "first-time pair, wrong
    // code → clear everything" branch. Once `piczoo_has_paired_before` is
    // set, it's ONLY cleared by explicit unpair paths (force_disconnect WS
    // message, 3-tap manual escape, URL-code-mismatch). This is the sticky
    // guarantee behind "once paired, remain paired forever".
    const wasPreviouslyPaired = !!(
      localStorage.getItem('piczoo_kiosk_id') ||
      localStorage.getItem('piczoo_has_paired_before')
    );

    try {
      const response = await axios.post(`${API}/kiosks/pair`, { pairing_code: code });
      setConfig(response.data);
      setKioskId(response.data.kiosk_id);
      localStorage.setItem('piczoo_kiosk_id', response.data.kiosk_id);
      localStorage.setItem('piczoo_pairing_code', code);
      // Sticky flag — survives any accidental clearing of the id/code keys.
      localStorage.setItem('piczoo_has_paired_before', '1');
      // Clear any pending retry — we're back online
      if (pairRetryTimeoutRef.current) {
        clearTimeout(pairRetryTimeoutRef.current);
        pairRetryTimeoutRef.current = null;
      }
      boothLogger.info('pair', 'success', 'Booth paired with backend', {
        kiosk_id: response.data.kiosk_id,
        code,
        was_previously_paired: wasPreviouslyPaired,
      });
      connectWebSocket(response.data.kiosk_id, code);
      setScreen(SCREENS.IDLE);
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || 'Invalid pairing code';
      setPairingError(detail);
      boothLogger.warn('pair', 'failure', `Pair failed: ${detail}`, {
        status,
        was_previously_paired: wasPreviouslyPaired,
        code,
      });
      pushBufferedError({
        level: wasPreviouslyPaired ? 'warn' : 'error',
        code: `PAIR_${status || 'NETWORK'}`,
        title: wasPreviouslyPaired ? 'Reconnecting to backend' : 'Pairing failed',
        message: detail,
        data: { status, body: err.response?.data, code, wasPreviouslyPaired },
      });

      if (wasPreviouslyPaired) {
        // The booth was previously paired. Normally we NEVER auto-unpair —
        // per operator requirement "once paired stays paired unless admin
        // deletes on backend". BUT — if the backend explicitly answers a
        // 404 with an "Invalid pairing code" body, that IS the admin
        // deletion signal we've been trusting all along. Retrying the
        // same dead code every 10s forever gives the customer a locked
        // "Reconnecting" screen with no path forward. Detect that one
        // signal and fall through to the fresh-pair flow so the operator
        // can type the new code the admin generated.
        //
        // Any other error (5xx, network, timeout, other 4xx) is still
        // treated as transient and retried indefinitely — those DO mean
        // "backend is temporarily unhappy, we should wait it out".
        const isAdminUnpairSignal =
          status === 404 &&
          /invalid pairing code/i.test(String(detail || ''));

        if (isAdminUnpairSignal) {
          console.warn('Backend rejected saved pairing code (404 "Invalid pairing code") — admin has clearly unpaired this booth. Clearing local state so operator can re-pair.');
          boothLogger.warn('pair', 'admin_unpair_detected', 'Backend returned 404 Invalid pairing code — clearing local state', { code });
          localStorage.removeItem('piczoo_kiosk_id');
          localStorage.removeItem('piczoo_pairing_code');
          localStorage.removeItem('piczoo_has_paired_before');
          setKioskId(null);
          setPairingCode('');
          setPairingError('This booth was unpaired by the admin. Enter the new pairing code shown in the dashboard.');
          setScreen(SCREENS.PAIRING);
          return;
        }

        // Everything else: treat as transient. Show Reconnecting, retry
        // every 10 seconds forever, keep the saved code.
        console.warn(`Pair failed (${status || 'network'}) on previously-paired booth — reconnecting forever, keeping saved code`);
        setScreen(SCREENS.RECONNECTING);
        if (!pairRetryTimeoutRef.current) {
          pairRetryTimeoutRef.current = setTimeout(() => {
            pairRetryTimeoutRef.current = null;
            handlePairingWithCode(code);
          }, 10000);
        }
      } else {
        // First-time pairing attempt with a wrong/unregistered code. Only NOW
        // do we treat a 4xx as definitive — because there's nothing to preserve
        // (localStorage is empty) and the guest needs to type a correct code.
        // Transient errors on first-pair still auto-retry.
        const isDefinitiveRejection = status && status >= 400 && status < 500;
        if (!isDefinitiveRejection && code) {
          if (!pairRetryTimeoutRef.current) {
            pairRetryTimeoutRef.current = setTimeout(() => {
              pairRetryTimeoutRef.current = null;
              handlePairingWithCode(code);
            }, 5000);
          }
        }
      }
    }
  };

  const handlePairing = async () => {
    await handlePairingWithCode(pairingCode);
  };

  // v3.12.0 — Start the ffmpeg-based native camera feed. Subscribes to
  // IPC frames and paints them into the <img> preview. On ANY failure we
  // fall back to WebRTC so a broken ffmpeg never strands a paying customer.
  const startNativeCameraFeed = async (skipWebrtcFallback = false) => {
    const api = window.piczooKiosk;
    if (isStartingCameraRef.current) {
      boothLogger.debug('camera', 'native_start_in_flight', 'startNativeCameraFeed skipped — already starting');
      return false;
    }
    isStartingCameraRef.current = true;
    let fallbackToWebrtc = false;
    let ok = false;
    try {
      const res = await api.nativeCameraStart();
      if (!res?.success) {
        boothLogger.error('camera', 'native_start_failed', `Native camera failed: ${res?.error || 'unknown'} — falling back to WebRTC`, res);
        nativeCameraModeRef.current = false;
        setNativeCameraMode(false);
        fallbackToWebrtc = true;
      } else {
        if (!nativeFeedActiveRef.current) {
          nativeFeedActiveRef.current = true;
          api.onNativeCameraFrame((frame) => {
            nativeLatestFrameRef.current = frame;
            try {
              const url = URL.createObjectURL(new Blob([frame], { type: 'image/jpeg' }));
              if (nativeImgRef.current) nativeImgRef.current.src = url;
              if (nativeFrameUrlRef.current) URL.revokeObjectURL(nativeFrameUrlRef.current);
              nativeFrameUrlRef.current = url;
            } catch (_e) { /* noop */ }
          });
        }
        boothLogger.info('camera', 'native_stream_started', `ffmpeg camera streaming (${res.device || 'default device'})`, {
          device: res.device,
          already_running: !!res.already_running,
        });
        if (currentError?.type === ERROR_TYPES.CAMERA) setCurrentError(null);
        cameraStatusRef.current = 'ok';
        ok = true;
      }
    } catch (e) {
      boothLogger.error('camera', 'native_start_threw', `Native camera start threw: ${e?.message || e} — falling back to WebRTC`, { stack: e?.stack });
      nativeCameraModeRef.current = false;
      setNativeCameraMode(false);
      fallbackToWebrtc = true;
    } finally {
      isStartingCameraRef.current = false;
    }
    if (fallbackToWebrtc && !skipWebrtcFallback) return startCamera();
    return ok;
  };

  const startCamera = async () => {
    // v3.12.0 — Native mode: ffmpeg capture, zero Chromium WebRTC.
    if (nativeCameraModeRef.current && window.piczooKiosk?.nativeCameraStart) {
      return startNativeCameraFeed();
    }
    // IN-FLIGHT LOCK: guarantees only one getUserMedia() call is pending at a
    // time. Multiple callers (payment-poll `then`, mount useEffect, retry
    // recovery) can race here before streamRef is set — the active-stream
    // guard below is not enough on its own because it only helps AFTER the
    // first call resolves.
    if (isStartingCameraRef.current) {
      boothLogger.debug('camera', 'start_in_flight', 'startCamera skipped — another call already in flight');
      return;
    }

    // IDEMPOTENCY GUARD: if a stream is already live and has active tracks,
    // just re-attach it to the video element instead of creating a new one.
    // Prevents the double-getUserMedia race that leaked a second GPU-backed
    // video decode pipeline every session (visible as duplicate
    // "camera/stream_started" events at the same timestamp — a real
    // contributor to the OOM native crash on low-spec kiosk hardware).
    if (streamRef.current && streamRef.current.active) {
      const tracks = streamRef.current.getVideoTracks?.() || [];
      const anyLive = tracks.some(t => t.readyState === 'live' && t.enabled);
      if (anyLive) {
        boothLogger.debug('camera', 'reuse_existing', 'startCamera called but stream already live — reusing');
        if (videoRef.current && videoRef.current.srcObject !== streamRef.current) {
          videoRef.current.srcObject = streamRef.current;
        }
        try { if (videoRef.current?.paused) await videoRef.current.play(); } catch (_e) { /* noop */ }
        // Clear any camera error banner since we're clearly working
        if (currentError?.type === ERROR_TYPES.CAMERA) setCurrentError(null);
        return;
      }
    }

    isStartingCameraRef.current = true;
    try {
      // Cap at 720p unconditionally.
      //
      // The July 31 GLADIA log (heap = 2072MB, plenty of RAM) proved that
      // 1080p is NOT bounded by JS heap — but the renderer STILL froze at
      // the drawImage() step. On low-end kiosk GPUs the compositor stalls
      // while decoding a 1920×1080 frame texture, which deadlocks the
      // main-thread drawImage() call. The photo strip prints at
      // 1000×650 anyway (see FIXED_PHOTO_LAYOUT — the video is
      // downscaled to fit), so 720p costs us NOTHING in print quality
      // and halves the per-frame texture upload / readback.
      //
      // Adaptive resolution logic kept as commented-out reference in case
      // we ever need a targeted A/B on hardware that clearly benefits
      // from higher res — but the default is now the safe path.
      const videoConstraints = { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'user' };
      let heapLimitMB = 0;
      try { heapLimitMB = Math.round((performance?.memory?.jsHeapSizeLimit || 0) / 1024 / 1024); } catch (_e) { /* noop */ }
      boothLogger.debug('camera', 'constraints_chosen', `heap=${heapLimitMB}MB → 720p (capped for GPU stability)`, {
        heapLimitMB, resolution: '720p',
      });
      // Use retry with backoff for camera initialization
      const stream = await retryWithBackoff(
        async () => {
          return await navigator.mediaDevices.getUserMedia({ video: videoConstraints });
        },
        {
          maxRetries: 3,
          initialDelay: 500,
          onRetry: (attempt, error) => {
            console.log(`Camera retry attempt ${attempt}:`, error.message);
          }
        }
      );
      
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        try {
          await videoRef.current.play();
        } catch (playErr) {
          // AbortError fires when a subsequent load() interrupts play().
          // This is harmless — the next play() attempt (from the video ref
          // callback or a retry) will succeed. Suppress so operators don't
          // see a red overlay for a self-healing race.
          if (playErr && (playErr.name === 'AbortError' || /interrupted by/i.test(playErr.message || ''))) {
            console.log('Camera play() aborted by reload (harmless):', playErr.message);
            boothLogger.debug('camera', 'play_aborted', 'video.play() aborted by reload (harmless)', { message: playErr.message });
          } else {
            throw playErr;
          }
        }
      }
      const track = stream.getVideoTracks?.()[0];
      const settings = track?.getSettings?.() || {};
      boothLogger.info('camera', 'stream_started', 'getUserMedia stream acquired', {
        width: settings.width,
        height: settings.height,
        deviceId: settings.deviceId?.slice(-8),
        facingMode: settings.facingMode,
      });
      nativeFallbackTriedRef.current = false;
      cameraStatusRef.current = 'ok';
      
      // Clear any camera errors
      if (currentError?.type === ERROR_TYPES.CAMERA) {
        setCurrentError(null);
      }
      
    } catch (err) {
      // Same guard at the outer level — the retry helper can also surface
      // AbortError. Never escalate this class of race to a UI overlay.
      if (err && (err.name === 'AbortError' || /interrupted by/i.test(err.message || ''))) {
        console.log('Camera setup aborted (harmless race, will self-heal):', err.message);
        boothLogger.debug('camera', 'setup_aborted', 'Camera setup aborted (harmless)', { message: err.message });
        return;
      }
      console.error('Camera error:', err);
      boothLogger.error('camera', 'setup_failed', `getUserMedia failed: ${err?.message || err}`, {
        name: err?.name,
        stack: err?.stack,
      });
      cameraStatusRef.current = 'failed';
      // Device snapshot at the moment of failure — shows whether Windows
      // still sees ANY camera (software issue) or none at all (USB drop).
      try {
        const devs = await navigator.mediaDevices.enumerateDevices();
        const cams = devs.filter((d) => d.kind === 'videoinput');
        boothLogger.error('camera', 'device_snapshot', `Devices at failure: ${cams.length} camera(s) visible to Windows`, {
          cameras: cams.map((c) => c.label || (c.deviceId || '').slice(-8) || 'unknown'),
          total_devices: devs.length,
        });
      } catch (_e) { /* noop */ }
      // AUTO-FAILOVER: Chromium lost sight of the camera (USB suspend /
      // re-enumeration) but ffmpeg/DirectShow can often still open it — and
      // the probe itself wakes a suspended device. Try once before the
      // fatal screen.
      if (err?.name === 'NotFoundError' && window.piczooKiosk?.nativeCameraStart &&
          !nativeCameraModeRef.current && !nativeFallbackTriedRef.current) {
        nativeFallbackTriedRef.current = true;
        boothLogger.warn('camera', 'native_fallback_attempt', 'WebRTC sees no camera — trying native ffmpeg failover');
        isStartingCameraRef.current = false;
        const ok = await startNativeCameraFeed(true);
        if (ok) {
          nativeCameraModeRef.current = true;
          setNativeCameraMode(true);
          boothLogger.info('camera', 'native_mode_enabled', 'Auto-failover: ffmpeg camera active after WebRTC NotFoundError');
          return;
        }
        boothLogger.error('camera', 'native_fallback_failed', 'ffmpeg failover also found no usable camera');
      }
      handleError(err, 'camera');
    } finally {
      isStartingCameraRef.current = false;
    }
  };

  const stopCamera = () => {
    // v3.12.0 — Tear down the native ffmpeg feed if it's running.
    if (nativeFeedActiveRef.current) {
      try { window.piczooKiosk?.nativeCameraStop?.(); } catch (_e) { /* noop */ }
      try { window.piczooKiosk?.offNativeCameraFrame?.(); } catch (_e) { /* noop */ }
      nativeFeedActiveRef.current = false;
      nativeLatestFrameRef.current = null;
      if (nativeFrameUrlRef.current) {
        try { URL.revokeObjectURL(nativeFrameUrlRef.current); } catch (_e) { /* noop */ }
        nativeFrameUrlRef.current = null;
      }
      boothLogger.info('camera', 'native_stream_stopped', 'Native ffmpeg camera feed stopped');
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    // Release the in-flight lock too — otherwise a subsequent restart after
    // an error stop would be silently skipped.
    isStartingCameraRef.current = false;
  };
  
  // Recover camera stream if lost
  const recoverCamera = async () => {
    try {
      await recoverCameraStream(videoRef, streamRef);
      setCurrentError(null);
    } catch (err) {
      handleError(err, 'camera');
    }
  };

  // CAMERA AUTO-RECOVERY — a USB webcam that power-blips re-enumerates within
  // seconds. While the camera error screen is up, probe every 5s and resume
  // the session the moment a camera is visible again. No human tap needed.
  useEffect(() => {
    if (screen !== SCREENS.ERROR || currentError?.type !== ERROR_TYPES.CAMERA) {
      setCameraRetryCountdown(null);
      return undefined;
    }
    setCameraRetryCountdown(5);
    let failedProbes = 0;
    const tick = setInterval(() => {
      setCameraRetryCountdown((c) => (c === null ? null : (c > 1 ? c - 1 : 5)));
    }, 1000);
    const probe = setInterval(async () => {
      try {
        let hasCamera = nativeCameraModeRef.current;
        if (!hasCamera) {
          const devices = await navigator.mediaDevices.enumerateDevices();
          hasCamera = devices.some((d) => d.kind === 'videoinput');
        }
        if (hasCamera) {
          boothLogger.info('camera', 'auto_recovered', 'Camera visible again — auto-resuming session');
          nativeFallbackTriedRef.current = false;
          handleErrorRetry();
        } else {
          failedProbes += 1;
          // Software "replug": after 10s still gone, ask Electron to restart
          // the camera's USB device (Restart-PnpDevice) — one shot per episode.
          if (failedProbes === 2 && window.piczooKiosk?.cameraUsbReplug) {
            boothLogger.warn('camera', 'usb_replug_attempt', 'Camera still missing after 10s — attempting software USB replug');
            window.piczooKiosk.cameraUsbReplug().then((r) => {
              boothLogger.info('camera', 'usb_replug_result', `USB replug ${r?.success ? 'SUCCEEDED' : 'failed'}`, r);
            }).catch(() => {});
          }
        }
      } catch (_e) { /* keep probing */ }
    }, 5000);
    return () => { clearInterval(tick); clearInterval(probe); };
  }, [screen, currentError, handleErrorRetry]);

  // Helper to get photo slot aspect ratio
  const getPhotoSlotAspectRatio = useCallback(() => {
    const slot = FIXED_PHOTO_LAYOUT.photoSlots[0];
    return { width: slot.width, height: slot.height, ratio: slot.width / slot.height };
  }, []);

  // Effect to handle countdown
  useEffect(() => {
    if (countdown === null || countdown === 0) return;
    boothLogger.debug('capture', 'countdown_tick', `Countdown tick: ${countdown}`, {
      countdown,
      screen,
      photo_index: photos.length,
    });
    const timer = setTimeout(() => {
      if (countdown === 1) {
        performCapture();
      } else {
        setCountdown(countdown - 1);
      }
    }, 1000);
    
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdown]);

  const performCapture = useCallback(async () => {
    boothLogger.info('capture', 'begin', `performCapture invoked for photo #${currentPhotoIndex + 1}`, {
      photoIndex: currentPhotoIndex,
      hasVideo: !!videoRef.current,
      hasCanvas: !!canvasRef.current,
    });
    // Flush immediately so if we hang inside a native path below, this
    // "begin" log makes it to the backend before the freeze.
    boothLogger.flush();

    // v3.12.0 — NATIVE CAPTURE PATH. Decode the freshest ffmpeg JPEG and
    // crop it with the identical slot math. No <video>, no drawImage from
    // a WebRTC surface, no GPU texture — the crash path is unreachable.
    if (nativeCameraModeRef.current) {
      try {
        let frame = nativeLatestFrameRef.current;
        if (!frame && window.piczooKiosk?.nativeCameraCapture) {
          const res = await window.piczooKiosk.nativeCameraCapture();
          if (res?.success) frame = res.dataUrl;
        }
        if (!frame) {
          boothLogger.warn('capture', 'native_no_frame', 'No native frame available — retrying countdown');
          setCountdown(null);
          setTimeout(() => setCountdown(3), 800);
          return;
        }
        const img = new Image();
        const objectUrl = typeof frame === 'string' ? null : URL.createObjectURL(new Blob([frame], { type: 'image/jpeg' }));
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = () => reject(new Error('native_frame_decode_failed'));
          img.src = objectUrl || frame;
        });
        const iw = img.naturalWidth, ih = img.naturalHeight;
        const nSlot = getPhotoSlotAspectRatio();
        const nTarget = nSlot.ratio;
        const nRatio = iw / ih;
        let nsx = 0, nsy = 0, nsw = iw, nsh = ih;
        if (nRatio > nTarget) { nsw = ih * nTarget; nsx = (iw - nsw) / 2; }
        else if (nRatio < nTarget) { nsh = iw / nTarget; nsy = (ih - nsh) / 2; }
        const now = nSlot.width * 2, noh = nSlot.height * 2;
        const nCanvas = document.createElement('canvas');
        nCanvas.width = now; nCanvas.height = noh;
        const nCtx = nCanvas.getContext('2d');
        // Mirror to match the mirrored on-screen preview (same as WebRTC path)
        nCtx.translate(now, 0);
        nCtx.scale(-1, 1);
        nCtx.drawImage(img, nsx, nsy, nsw, nsh, 0, 0, now, noh);
        const nData = nCanvas.toDataURL('image/jpeg', 0.9);
        if (objectUrl) { try { URL.revokeObjectURL(objectUrl); } catch (_e) { /* noop */ } }
        try { nCanvas.width = 0; nCanvas.height = 0; } catch (_e) { /* noop */ }
        boothLogger.info('capture', 'native_success', `Photo #${currentPhotoIndex + 1} captured via native helper`, {
          photoIndex: currentPhotoIndex,
          sizeKB: Math.round(nData.length / 1024),
          frameW: iw, frameH: ih,
        });
        setShowFlash(true);
        setTimeout(() => setShowFlash(false), 300);
        setCapturedPhoto(nData);
        setCountdown(null);
        setScreen(SCREENS.PHOTO_REVIEW);
      } catch (e) {
        boothLogger.error('capture', 'native_capture_failed', `Native capture failed: ${e?.message || e}`, { stack: e?.stack });
        setCountdown(null);
        setTimeout(() => setCountdown(3), 800);
      }
      return;
    }

    if (!videoRef.current || !canvasRef.current) {
      boothLogger.warn('capture', 'refs_missing', 'video or canvas ref is null', {
        hasVideo: !!videoRef.current,
        hasCanvas: !!canvasRef.current,
      });
      return;
    }

    const video = videoRef.current;
    const canvas = canvasRef.current;

    // Defensive guards — the countdown timer can fire in edge cases where the
    // video isn't fully ready (metadata not loaded, track ended between the
    // 3-2-1 tick and performCapture, permission revoked mid-session, etc.).
    // Without these guards `video.videoWidth = 0` produces NaN in the ratio
    // math and `drawImage` throws IndexSizeError — which crashes the whole
    // renderer (white screen). Silently restart the countdown instead.
    const videoWidth = video.videoWidth;
    const videoHeight = video.videoHeight;
    if (!videoWidth || !videoHeight || videoWidth <= 0 || videoHeight <= 0) {
      boothLogger.warn('capture', 'video_not_ready', `Video not ready: ${videoWidth}x${videoHeight}`, {
        videoWidth, videoHeight,
        readyState: video.readyState,
        streamActive: streamRef.current?.active,
        tracks: streamRef.current?.getVideoTracks?.().map(t => ({ enabled: t.enabled, readyState: t.readyState })),
      });
      console.warn(`Capture aborted: video not ready (${videoWidth}x${videoHeight}) — retrying countdown`);
      setCountdown(null);
      setTimeout(() => {
        if (videoRef.current && videoRef.current.videoWidth > 0) {
          setCountdown(3);
        } else {
          recoverCamera().catch(() => {});
        }
      }, 500);
      return;
    }

    let ctx;
    try {
      ctx = canvas.getContext('2d');
    } catch (e) {
      boothLogger.error('capture', 'ctx_failed', `getContext threw: ${e?.message || e}`, { stack: e?.stack });
      console.error('Canvas context unavailable:', e);
      return;
    }
    if (!ctx) {
      boothLogger.error('capture', 'ctx_null', 'canvas.getContext(2d) returned null');
      return;
    }

    const slotAspect = getPhotoSlotAspectRatio();
    const targetRatio = slotAspect.ratio;
    const videoRatio = videoWidth / videoHeight;

    let sourceX = 0, sourceY = 0, sourceWidth = videoWidth, sourceHeight = videoHeight;

    if (videoRatio > targetRatio) {
      sourceWidth = videoHeight * targetRatio;
      sourceX = (videoWidth - sourceWidth) / 2;
    } else if (videoRatio < targetRatio) {
      sourceHeight = videoWidth / targetRatio;
      sourceY = (videoHeight - sourceHeight) / 2;
    }

    const outputWidth = slotAspect.width * 2;
    const outputHeight = slotAspect.height * 2;
    canvas.width = outputWidth;
    canvas.height = outputHeight;

    // FRAME-READY GUARD — the July GLADIA log showed the renderer
    // freezing at drawImage on 1080p input. drawImage(video) is a
    // synchronous call that internally waits for the compositor to
    // produce a decoded frame. On low-end GPUs the compositor can
    // stall, causing a main-thread deadlock. requestVideoFrameCallback
    // (Chromium 83+) fires only after a new decoded frame is available,
    // so drawImage is guaranteed to have something to consume. 2s
    // timeout guards against rvfc itself not firing (broken driver /
    // paused track).
    if (typeof video.requestVideoFrameCallback === 'function') {
      boothLogger.info('capture', 'awaiting_frame', 'Waiting for requestVideoFrameCallback');
      boothLogger.flush();
      try {
        await new Promise((resolve, reject) => {
          const to = setTimeout(() => reject(new Error('rvfc_timeout_2s')), 2000);
          video.requestVideoFrameCallback(() => { clearTimeout(to); resolve(); });
        });
        boothLogger.info('capture', 'frame_ready', 'Fresh video frame decoded — safe to drawImage');
      } catch (_e) {
        boothLogger.warn('capture', 'rvfc_timeout', 'requestVideoFrameCallback did not fire in 2s — proceeding');
      }
    } else {
      boothLogger.debug('capture', 'no_rvfc', 'requestVideoFrameCallback unsupported — drawImage directly');
    }

    let photoData;
    try {
      // GRANULAR INSTRUMENTATION — the July log showed the renderer
      // freezing between `capture/begin` and `capture/success` with
      // NOTHING in between. Whichever of these steps is the last one
      // logged before the next silent hang IS the culprit. Flushing
      // synchronously so a subsequent hang can't swallow the pointer.
      boothLogger.info('capture', 'draw_before', 'Entering drawImage', {
        sourceX, sourceY, sourceWidth, sourceHeight,
        outputWidth, outputHeight,
        videoReadyState: video.readyState,
        videoCurrentTime: video.currentTime,
      });
      boothLogger.flush();

      ctx.translate(outputWidth, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(video, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, outputWidth, outputHeight);

      boothLogger.info('capture', 'draw_after', 'drawImage returned — entering toDataURL');
      boothLogger.flush();

      photoData = canvas.toDataURL('image/jpeg', 0.9);

      boothLogger.info('capture', 'encode_after', `toDataURL returned ${Math.round(photoData.length / 1024)}KB`);
      // CRITICAL memory hygiene: after we've encoded the JPEG, immediately
      // release the canvas backing buffer. Without this, Chromium keeps a
      // 1000×650×4 = 2.6MB texture allocated PER capture, and the GPU-side
      // buffers accumulate across the 4 photos of a session. On lower-spec
      // kiosk hardware this drives the renderer process OOM and triggers
      // the native crash we've been chasing (no JS exception, no
      // ErrorBoundary catch — Electron just reloads the page).
      try { canvas.width = 0; canvas.height = 0; } catch (_e) { /* noop */ }
    } catch (e) {
      console.error('Photo capture draw/encode failed:', e);
      boothLogger.error('capture', 'draw_failed', `drawImage/toDataURL threw: ${e?.message || e}`, {
        stack: e?.stack,
        name: e?.name,
        sourceX, sourceY, sourceWidth, sourceHeight,
        outputWidth, outputHeight,
        videoRatio, targetRatio,
      });
      setCountdown(null);
      try {
        const kid = localStorage.getItem('piczoo_kiosk_id');
        if (kid && process.env.REACT_APP_BACKEND_URL) {
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/kiosks/${kid}/crash-report`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
              message: `performCapture: ${e.message || String(e)}`,
              stack: e.stack || '',
              screen: 'photo_capture',
              url: window.location.href,
              user_agent: navigator.userAgent,
            }),
          }).catch(() => {});
        }
      } catch (_e) { /* noop */ }
      setTimeout(() => setCountdown(3), 800);
      return;
    }

    // Flash effect
    setShowFlash(true);
    setTimeout(() => setShowFlash(false), 300);

    boothLogger.info('capture', 'success', `Photo #${currentPhotoIndex + 1} captured`, {
      photoIndex: currentPhotoIndex,
      sizeKB: Math.round(photoData.length / 1024),
      videoWidth, videoHeight,
      outputWidth, outputHeight,
    });
    // Immediate memory snapshot at capture success — fires BEFORE the crash
    // can happen. If we're seeing memory near limit here, that's the smoking
    // gun regardless of whether the crash is on photo 1, 2, 3, or 4.
    try {
      const mem = performance?.memory;
      if (mem) {
        boothLogger.info('lifecycle', 'memory_at_capture', `Heap ${Math.round(mem.usedJSHeapSize/1024/1024)}/${Math.round(mem.jsHeapSizeLimit/1024/1024)}MB after photo ${currentPhotoIndex + 1}`, {
          used_MB: Math.round(mem.usedJSHeapSize / 1024 / 1024),
          total_MB: Math.round(mem.totalJSHeapSize / 1024 / 1024),
          limit_MB: Math.round(mem.jsHeapSizeLimit / 1024 / 1024),
          pct_used: Math.round((mem.usedJSHeapSize / mem.jsHeapSizeLimit) * 100),
          photo_index: currentPhotoIndex + 1,
        });
      }
    } catch (_e) { /* noop */ }

    // Store captured photo and show review screen
    setCapturedPhoto(photoData);
    setCountdown(null);
    setScreen(SCREENS.PHOTO_REVIEW);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getPhotoSlotAspectRatio, currentPhotoIndex]);

  const startCountdown = () => {
    // Log the request so activity logs can distinguish "user tapped
    // capture" from "auto-countdown fired" from "button never
    // registered". Previous silence here made a real production issue
    // impossible to diagnose from logs alone.
    boothLogger.info('capture', 'countdown_requested', `Countdown requested for photo #${photos.length + 1}`, {
      photo_index: photos.length,
      trigger: 'manual_button',
    });
    // Functional setter — atomically read latest state to avoid
    // clobbering an in-flight countdown (e.g., auto-countdown poll
    // fires at the same instant as a manual button tap). If a
    // countdown is already running, keep it going.
    setCountdown((prev) => (prev === null ? 3 : prev));
  };

  // NEW: Handle keeping the photo
  const handleKeepPhoto = () => {
    boothLogger.info('capture', 'keep_photo', `User kept photo #${photos.length + 1}`, {
      photo_index: photos.length,
      photos_so_far: photos.length,
      target: FIXED_PHOTO_LAYOUT.numPhotos,
    });
    const newPhotos = [...photos, capturedPhoto];
    setPhotos(newPhotos);
    setCapturedPhoto(null);
    setRetakesLeft(1); // Reset retakes for next photo
    
    // Cache photo to IndexedDB immediately for crash resilience.
    // .catch() guards against the rare case where IndexedDB itself throws
    // synchronously — the cache utility is best-effort and never blocks UX.
    if (kioskId) {
      cachePhoto(kioskId, newPhotos.length - 1, capturedPhoto).catch(() => {});
    }
    
    if (newPhotos.length >= FIXED_PHOTO_LAYOUT.numPhotos) {
      // All photos captured - go to preview
      stopCamera();
      setScreen(SCREENS.PREVIEW);
    } else {
      // More photos to capture - camera stream still active.
      // Flag the mount-time effect on PHOTO_CAPTURE to auto-fire the 3-2-1
      // countdown so the user doesn't have to tap "capture" 4 separate
      // times (this was reported as "the booth hangs after photo 1 —
      // countdown for photo 2 never starts"). The flag is consumed once,
      // inside that effect.
      autoStartCountdownRef.current = true;
      setCurrentPhotoIndex(currentPhotoIndex + 1);
      setScreen(SCREENS.PHOTO_CAPTURE);
      // Re-attach stream to video element (handled by useEffect)
    }
  };

  // NEW: Handle retaking the photo
  const handleRetakePhoto = () => {
    boothLogger.info('capture', 'retake_photo', `User retook photo #${photos.length + 1}`, {
      photo_index: photos.length,
      retakes_left_before: retakesLeft,
    });
    if (retakesLeft > 0) {
      setRetakesLeft(retakesLeft - 1);
      setCapturedPhoto(null);
      // Same auto-countdown behaviour on retake — the user has already
      // committed to shooting this frame, no reason to make them tap again.
      autoStartCountdownRef.current = true;
      setScreen(SCREENS.PHOTO_CAPTURE);
      // Re-attach stream to video element (handled by useEffect)
    }
  };

  const handleSelectQuantity = (qty) => {
    setQuantity(qty);
  };

  const handleProceedToPayment = () => {
    setPaymentTimer(90);
    setScreen(SCREENS.PAYMENT);
    createPaymentOrder();
  };

  const createPaymentOrder = async (attempt = 0) => {
    // Single-flight lock: one session = one order = one immutable QR.
    // No re-render, double-tap or stray retrigger may create a twin order
    // (a twin silently swaps the on-screen QR — see Mykos 12/09 incident).
    if (attempt === 0 && (creatingOrderRef.current || orderId)) return;
    creatingOrderRef.current = true;
    const seq = attempt === 0 ? ++orderSeqRef.current : orderSeqRef.current;
    const prices = resolveActivePricing(config.pricing);
    const price = quantity === 1 ? prices.one_unit_price : 
                  quantity === 2 ? prices.two_unit_price : 
                  prices.three_unit_price;
    
    // Reset any prior QR image state so the spinner shows again on retry.
    setQrImageLoaded(false);
    setOrderError(null);
    boothLogger.info('payment', 'order_create_begin', `Creating order for ${quantity} × ₹${price}`, { quantity, amount: price, attempt });
    try {
      const response = await axios.post(`${API}/payments/create-order`, {
        kiosk_id: kioskId,
        quantity,
        amount: price
      });
      if (seq !== orderSeqRef.current) {
        // A voucher order (or session reset) superseded this request while it
        // was in flight — never overwrite the live orderId. Close the orphan.
        creatingOrderRef.current = false;
        boothLogger.warn('payment', 'order_create_stale', `Discarding stale order ${response.data.order_id} — superseded mid-flight`, {
          order_id: response.data.order_id,
        });
        axios.post(`${API}/payments/${response.data.order_id}/cancel`).catch(() => {});
        return;
      }
      setOrderId(response.data.order_id);
      setQrCode(response.data.qr_code);
      creatingOrderRef.current = false;
      boothLogger.info('payment', 'order_created', `Order ${response.data.order_id} created`, {
        order_id: response.data.order_id,
        amount: price,
        quantity,
        has_qr: !!response.data.qr_code,
      });
    } catch (err) {
      const status = err?.response?.status;
      // Transient server/network blip: retry once automatically after 2s
      // before bothering the customer — most 5xx blips clear immediately.
      if (attempt === 0 && (!status || status >= 500)) {
        boothLogger.warn('payment', 'order_create_retry', `Order create hit ${status || 'network error'} — auto-retrying once`, { status });
        setTimeout(() => createPaymentOrder(1), 2000);
        return;
      }
      // Surface the failure to the UI instead of leaving the spinner
      // stuck. Booth staff can tap Retry without a full reboot.
      creatingOrderRef.current = false;
      console.error('Failed to create payment order:', err);
      const detail = err?.response?.data?.detail || 'Could not reach payment provider. Please retry.';
      setOrderError(detail);
      boothLogger.error('payment', 'order_create_failed', `Order create failed: ${detail}`, {
        status,
        body: err?.response?.data,
      });
      try { toast.error(detail); } catch (_) {}
    }
  };

  // Apply a voucher: creates a REPLACEMENT order carrying the code (backend
  // validates + atomically holds/uses the voucher), then cancels the prior
  // code-less order so its Razorpay QR can't be paid anymore. Free (100%)
  // vouchers come back status=paid — the normal status poll picks that up
  // and drives the exact same success path as a real payment. No parallel
  // logic, no oversight gaps: holds release via the existing cancel/timeout/
  // reset flows because orderId is always the single source of truth.
  const applyVoucher = async () => {
    const code = voucherInput.trim().toUpperCase();
    if (!code || voucherApplying) return;
    setVoucherApplying(true);
    setVoucherError(null);
    const prices = resolveActivePricing(config.pricing);
    const price = quantity === 1 ? prices.one_unit_price :
                  quantity === 2 ? prices.two_unit_price :
                  prices.three_unit_price;
    const previousOrderId = orderId;
    try {
      const response = await axios.post(`${API}/payments/create-order`, {
        kiosk_id: kioskId,
        quantity,
        amount: price,
        voucher_code: code,
      });
      const d = response.data;
      // Invalidate any still-in-flight base-order create — its late response
      // must never clobber this voucher order (stranded-session race).
      orderSeqRef.current += 1;
      // Kill the old code-less order/QR (releases nothing — it carried no
      // voucher — but closes its Razorpay QR server-side).
      if (previousOrderId && previousOrderId !== d.order_id) {
        axios.post(`${API}/payments/${previousOrderId}/cancel`).catch(() => {});
      }
      setAppliedVoucher({
        code: d.voucher_code || code,
        discount: d.discount_amount || 0,
        finalAmount: d.amount,
        isFree: !!d.is_free,
      });
      setQrImageLoaded(false);
      setOrderId(d.order_id);
      setQrCode(d.qr_code || null);
      setVoucherOpen(false);
      setVoucherInput('');
      boothLogger.info('payment', 'voucher_applied', `Voucher ${code} applied — ₹${d.discount_amount} off, pay ₹${d.amount}${d.is_free ? ' (FREE)' : ''}`, {
        order_id: d.order_id, code, discount: d.discount_amount, final: d.amount, is_free: !!d.is_free,
      });
      if (d.is_free) {
        // Instant green tick; the status poll on the new orderId confirms
        // 'paid' and performs the standard navigation to capture.
        setPaymentStatus('success');
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not verify the code. Please retry.';
      setVoucherError(detail.replace(/^Voucher rejected:\s*/i, ''));
      boothLogger.warn('payment', 'voucher_rejected', `Voucher ${code} rejected: ${detail}`, { code, status: err?.response?.status });
    } finally {
      setVoucherApplying(false);
    }
  };

  // NOTE: The old handleMockPayment / handlePaymentVerify manual button was
  // removed in iter22 per user feedback. Payment success is picked up
  // exclusively via the /status poll useEffect (line ~421). No customer
  // action is needed after they finish paying — the booth auto-detects
  // + auto-navigates within 1-3 seconds of Razorpay confirming.

  // NEW: Generate strip image for saving
  const generateStripImage = async () => {
    const canvas = document.createElement('canvas');
    canvas.width = FIXED_PHOTO_LAYOUT.stripWidth;
    canvas.height = FIXED_PHOTO_LAYOUT.stripHeight;
    const ctx = canvas.getContext('2d');
    
    // Get template config
    const templateConfig = config?.template_config || { backgroundColor: '#FFFFFF', customLogoUrl: null };
    
    // Fill background
    ctx.fillStyle = templateConfig.backgroundColor;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw photos
    for (let i = 0; i < photos.length; i++) {
      const slot = FIXED_PHOTO_LAYOUT.photoSlots[i];
      if (!slot) continue;
      
      const img = new Image();
      img.crossOrigin = 'anonymous';
      await new Promise((resolve) => {
        img.onload = resolve;
        img.src = photos[i];
      });
      
      ctx.drawImage(img, slot.x, slot.y, slot.width, slot.height);
    }
    
    // Draw custom logo if exists
    if (templateConfig.customLogoUrl) {
      // Same resolution rules as the admin editor + backend print path:
      // absolute (S3/CDN) and data/blob URLs pass through; only genuinely
      // relative paths get the backend host prefixed. Blindly prepending
      // BACKEND_URL to an absolute URL produced a broken src and flooded
      // diagnostics with "Failed to draw logo" on every preview.
      const raw = templateConfig.customLogoUrl;
      const logoSrc = (/^(https?:)?\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:'))
        ? raw
        : `${BACKEND_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = 'anonymous';
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = () => reject(new Error(`logo image failed to load: ${logoSrc}`));
          logoImg.src = logoSrc;
        });
        
        const logoMaxWidth = FIXED_PHOTO_LAYOUT.stripWidth * 0.8;
        const logoMaxHeight = FIXED_PHOTO_LAYOUT.brandingArea.height * 0.7;
        const scale = Math.min(logoMaxWidth / logoImg.width, logoMaxHeight / logoImg.height);
        const logoWidth = logoImg.width * scale;
        const logoHeight = logoImg.height * scale;
        const logoX = (FIXED_PHOTO_LAYOUT.stripWidth - logoWidth) / 2;
        const logoY = FIXED_PHOTO_LAYOUT.brandingArea.y + (FIXED_PHOTO_LAYOUT.brandingArea.height - logoHeight) / 2;
        
        ctx.drawImage(logoImg, logoX, logoY, logoWidth, logoHeight);
      } catch (e) {
        console.error(`Failed to draw logo: ${e?.message || e}`);
      }
    }
    
    return canvas.toDataURL('image/jpeg', 0.95);
  };

  const handlePrint = async () => {
    setScreen(SCREENS.PRINTING);
    let printedOk = false; // set once a print job actually reaches the printer
    
    // Log print to inventory
    try {
      await axios.post(`${API}/inventory/increment?kiosk_id=${kioskId}&count=${quantity}`);
    } catch (err) {
      console.error('Failed to log print:', err);
    }
    
    // Generate print image using backend (production-ready 1200x1800px @ 300 DPI)
    try {
      const response = await axios.post(`${API}/print/generate`, {
        photos: photos,
        kiosk_id: kioskId,
        order_id: orderId || null
      });
      
      const { print_url, print_landscape_url, strip_url, qr_code, session_id, help_qr } = response.data;

      // World-class print pipeline (v3.7.12+):
      //  * The backend ALSO renders an 1800×1200 landscape variant in the
      //    DNP DS-RX1's native input orientation.
      //  * If the booth software is recent enough to receive it, we send
      //    that URL with rotation=0 — meaning the booth performs ZERO
      //    client-side image processing (no hidden BrowserWindow, no
      //    capturePage, no DPR scaling). Eliminates all the asymmetry /
      //    aspect-ratio bugs that came from re-rendering on the client.
      //  * If `print_landscape_url` is missing (older backend), we fall
      //    back to the legacy print_url + client-side rotation path.
      const printerReadyUrl = print_landscape_url || print_url;
      const printerReadyRotation = print_landscape_url ? 0 : 90;

      setDownloadQrCode(qr_code);
      setDownloadUrl(`${BACKEND_URL}/download/${session_id}`);
      setHelpQr(help_qr || null);

      // Photos successfully uploaded — clear local cache
      if (kioskId) clearCachedSession(kioskId).catch(() => {});

      // Send print job to Electron app if available
      if (window.piczooKiosk?.printPhoto) {
        // v3.14.0 — ALWAYS send the job when the printer exists in Windows.
        // Previously a non-zero Win32 status (e.g. POWER_SAVE while the DNP
        // idles) silently SKIPPED paid print jobs. The spooler queues jobs
        // and wakes idle printers — exactly like every other print software.
        // Status is now a warning signal only; real failures surface from
        // the actual print attempt below.
        const printerStatus = await window.piczooKiosk.getPrinterStatus();

        if (!printerStatus.found) {
          console.error('Printer not found:', printerStatus);
          pushBufferedError({
            level: 'error',
            code: 'PRINTER_NOT_FOUND',
            title: 'Printer not found',
            message: 'The configured printer is not visible to Windows. Re-run setup or check USB.',
            data: printerStatus,
          });
          // Still show complete screen - photo can be downloaded digitally
        } else {
          if (!printerStatus.ready) {
            console.warn('Printer reports attention flags (printing anyway):', printerStatus.statusText);
            boothLogger.warn('print', 'printer_attention', `Printer flags: ${printerStatus.statusText || printerStatus.status} — attempting print anyway`, printerStatus);
          }
          // Send print job — retry up to 10 times (user-specified) before
          // surfacing an error. Transient network blips (e.g. ECONNRESET
          // while the helper downloads the print image) self-heal invisibly.
          const MAX_PRINT_ATTEMPTS = 10;
          let printResult = null;
          for (let attempt = 1; attempt <= MAX_PRINT_ATTEMPTS; attempt++) {
            try {
              printResult = await window.piczooKiosk.printPhoto({
                imageUrl: printerReadyUrl,
                imageData: printerReadyUrl,
                rotation: printerReadyRotation,
                copies: quantity,
                sessionId: session_id,
                orderId: orderId || ''
              });
            } catch (e) {
              printResult = { success: false, error: 'PRINT_IPC_ERROR', message: e?.message || String(e) };
            }
            if (printResult?.success) break;
            boothLogger.warn('print', 'attempt_failed', `Print attempt ${attempt}/${MAX_PRINT_ATTEMPTS} failed: ${printResult?.message || printResult?.error || 'unknown'} — ${attempt < MAX_PRINT_ATTEMPTS ? 'retrying' : 'giving up'}`, { attempt, error: printResult?.error });
            if (attempt < MAX_PRINT_ATTEMPTS) {
              await new Promise((r) => setTimeout(r, 2000));
            }
          }

          if (printResult.success) {
            console.log('Print job sent successfully:', printResult);
            printedOk = true;
            boothLogger.info('print', 'job_sent', `Print job sent (${quantity} cop${quantity === 1 ? 'y' : 'ies'})`, { partial: !!printResult.partial });
            if (printResult.partial) {
              pushBufferedError({
                level: 'warn',
                code: 'PRINT_PARTIAL',
                title: 'Print partially completed',
                message: printResult.message,
                data: printResult,
              });
            }
          } else {
            console.error('Print failed:', printResult);
            boothLogger.error('print', 'job_failed', `Print failed after ${MAX_PRINT_ATTEMPTS} attempts: ${printResult.message || printResult.error || 'unknown'}`, printResult);
            pushBufferedError({
              level: 'error',
              code: printResult.error || 'PRINT_FAILED',
              title: `Print failed (${MAX_PRINT_ATTEMPTS} attempts)`,
              message: printResult.message || 'Unknown print error',
              data: printResult,
            });
          }
        }
      } else {
        // Web browser - use postMessage for Electron IPC
        window.postMessage({
          type: 'piczoo_print',
          options: {
            imageUrl: printerReadyUrl,
            imageData: printerReadyUrl,
            rotation: printerReadyRotation,
            copies: quantity,
            sessionId: session_id,
            orderId: orderId || ''
          }
        }, '*');
        printedOk = true;
      }
      
    } catch (err) {
      console.error('Failed to generate print:', err);
      // Fallback to old method if new endpoint fails
      try {
        const stripImage = await generateStripImage();
        const response = await axios.post(`${API}/photo-sessions`, {
          kiosk_id: kioskId,
          photos: photos,
          strip_image: stripImage
        });
        setDownloadQrCode(response.data.qr_code);
        setDownloadUrl(response.data.download_url);
      } catch (fallbackErr) {
        console.error('Fallback also failed:', fallbackErr);
      }
    }
    
    // Show complete screen after printing delay.
    // Multi-copy orders: hold the PRINTING screen for the full estimated
    // duration (~35s per copy) with a staged per-copy tracker, so customers
    // don't walk away after the first print (venue complaint, Jun 2026).
    // DNP DS-RX1HS: 12.4s per 4x6 sheet (spec) + one-time ~15s overhead
    // (driver render, USB transfer, wake). Model: base + per-copy, where
    // per-copy is learned per booth from paper-out evidence (10-60s clamp).
    const PRINT_BASE_SECONDS = 15;
    const PER_COPY_SECONDS = Math.min(60, Math.max(10, Number(config?.print_timing?.per_copy_seconds) || 16));
    // Guard: if the session was reset (new customer) before the timer fires,
    // don't yank the new session to the COMPLETE screen.
    const mySessionSeq = orderSeqRef.current;
    if (printedOk && quantity > 1) {
      setPrintStages({ copies: quantity, base: PRINT_BASE_SECONDS, perCopy: PER_COPY_SECONDS, startedAt: Date.now() });
      setTimeout(() => {
        if (orderSeqRef.current !== mySessionSeq) return;
        setPrintStages(null);
        setScreen(SCREENS.COMPLETE);
        setCompleteTimer(30);
      }, (PRINT_BASE_SECONDS + quantity * PER_COPY_SECONDS) * 1000);
    } else {
      setTimeout(() => {
        if (orderSeqRef.current !== mySessionSeq) return;
        setScreen(SCREENS.COMPLETE);
        setCompleteTimer(30);
      }, 5000);
    }
  };

  const resetKiosk = () => {
    orderSeqRef.current += 1;
    setScreen(SCREENS.IDLE);
    setQuantity(1);
    setOrderId(null);
    setQrCode(null);
    setPaymentStatus('waiting');
    setPaymentTimer(90);
    setPollFailures(0);
    setPhotos([]);
    setCurrentPhotoIndex(0);
    setCountdown(null);
    setCapturedPhoto(null);
    setRetakesLeft(1);
    setStripPreview(null);
    setDownloadQrCode(null);
    setDownloadUrl(null);
    setHelpQr(null);
    setPrintStages(null);
    setVoucherOpen(false);
    setVoucherInput('');
    setVoucherError(null);
    setAppliedVoucher(null);
    if (kioskId) clearCachedSession(kioskId).catch(() => {});
    setCompleteTimer(30);
    stopCamera();
  };

  const handleExit = async () => {
    try {
      await axios.post(`${API}/kiosks/${kioskId}/verify-admin-password`, { password: exitPassword });
      window.close();
      window.location.href = '/';
    } catch {
      setExitError('Incorrect password');
    }
  };

  // 1s tick to animate the staged multi-copy print tracker
  useEffect(() => {
    if (screen === SCREENS.PRINTING && printStages) {
      const iv = setInterval(() => setPrintTick((t) => t + 1), 1000);
      return () => clearInterval(iv);
    }
  }, [screen, printStages]);

  // Admin-controlled print options: hide disabled quantities on the booth.
  // Missing/empty field = all enabled (backward compatible with old pricing docs).
  // Busy-hour schedules are resolved first so peak-hour overrides apply live.
  const activePricing = resolveActivePricing(config?.pricing);
  const enabledQuantities = (() => {
    const list = (activePricing?.enabled_quantities?.length
      ? activePricing.enabled_quantities : [1, 2, 3]).filter((q) => [1, 2, 3].includes(q));
    return list.length ? list : [1, 2, 3];
  })();

  useEffect(() => {
    if (screen === SCREENS.QUANTITY_SELECT && !enabledQuantities.includes(quantity)) {
      setQuantity(Math.min(...enabledQuantities));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, config?.pricing]);

  const getPrice = () => {
    if (!config?.pricing) return 0;
    const prices = resolveActivePricing(config.pricing);
    return quantity === 1 ? prices.one_unit_price : 
           quantity === 2 ? prices.two_unit_price : 
           prices.three_unit_price;
  };

  // Render screens
  const renderScreen = () => {
    // Derive theme from admin-configured background. This makes text
    // auto-contrast on any brand background the venue picks — no more
    // dark text on dark background (the visibility bug the operator hit).
    const theme = themeForBackground(config?.ui_config?.background_color);
    // Style spread applied to each screen root so utility classes below
    // inherit via CSS custom properties (see the <style> block below).
    const rootThemeStyle = {
      backgroundColor: theme.bg,
      color: theme.text,
      '--kiosk-text': theme.text,
      '--kiosk-muted': theme.muted,
      '--kiosk-border': theme.border,
    };
    switch (screen) {
      case SCREENS.RECONNECTING:
        return (
          <div
            className="flex flex-col items-center justify-center min-h-screen p-8 bg-cream-50"
            data-testid="reconnecting-screen"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-12 shadow-soft max-w-md w-full text-center"
            >
              <img src="/piczoo-logo.png" alt="Piczoo" className="h-12 mx-auto mb-6" />
              <div className="flex items-center justify-center gap-3 mb-4">
                <RefreshCw className="w-6 h-6 text-charcoal-800/50 animate-spin" />
                <h1 className="font-serif text-2xl text-charcoal-900">Reconnecting…</h1>
              </div>
              <p className="text-charcoal-800/60 mb-2">
                Booth is offline for a moment. We&apos;re keeping your pairing safe and reconnecting automatically.
              </p>
              <p className="text-xs text-charcoal-800/40 mt-4">
                Retrying every 10 seconds. Please leave the booth on — it will pick up on its own.
              </p>
              {pairingError && (
                <p
                  className="text-[10px] text-charcoal-800/30 mt-4 font-mono"
                  data-testid="reconnecting-detail"
                >
                  Last error: {pairingError}
                </p>
              )}
              {/* Manual escape hatch (3 taps within 3s) — for venue techs
                  who need to force a fresh pair from a physically present
                  booth. Hidden from casual guests: no visible affordance
                  beyond a tiny grey dot. */}
              <button
                onClick={() => {
                  const now = Date.now();
                  const ref = reconnectingTapCountRef.current;
                  if (now - ref.firstAt > 3000) {
                    ref.count = 1;
                    ref.firstAt = now;
                  } else {
                    ref.count += 1;
                  }
                  if (ref.count >= 3) {
                    ref.count = 0;
                    ref.firstAt = 0;
                    if (pairRetryTimeoutRef.current) {
                      clearTimeout(pairRetryTimeoutRef.current);
                      pairRetryTimeoutRef.current = null;
                    }
                    localStorage.removeItem('piczoo_kiosk_id');
                    localStorage.removeItem('piczoo_pairing_code');
                    localStorage.removeItem('piczoo_has_paired_before');
                    setKioskId(null);
                    setConfig(null);
                    setPairingCode('');
                    setPairingError('');
                    setScreen(SCREENS.PAIRING);
                  }
                }}
                aria-label="Manual re-pair (venue tech only)"
                className="mt-8 w-3 h-3 rounded-full bg-charcoal-800/10 hover:bg-charcoal-800/20 transition-colors mx-auto block"
                data-testid="reconnecting-escape-dot"
              />
            </motion.div>
          </div>
        );

      case SCREENS.PAIRING:
        return (
          <div className="flex flex-col items-center justify-center min-h-screen p-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-12 shadow-soft max-w-md w-full text-center"
            >
              <img src="/piczoo-logo.png" alt="Piczoo" className="h-12 mx-auto mb-6" />
              <p className="text-charcoal-800/60 mb-8">Enter pairing code to connect this kiosk</p>
              
              <input
                type="text"
                value={pairingCode}
                onChange={(e) => setPairingCode(e.target.value.toUpperCase())}
                placeholder="XXXXXXXX"
                maxLength={8}
                className="w-full text-center text-3xl font-mono tracking-widest px-6 py-4 bg-cream-50 border-2 border-charcoal-900/10 rounded-2xl focus:outline-none focus:border-charcoal-900/30 uppercase"
                data-testid="pairing-code-input"
              />
              
              {pairingError && <p className="text-red-500 mt-4">{pairingError}</p>}
              
              <button
                onClick={handlePairing}
                disabled={pairingCode.length < 8}
                className="btn-kiosk mt-6 disabled:opacity-50"
                data-testid="pair-kiosk-btn"
              >
                Connect Kiosk
              </button>
            </motion.div>
          </div>
        );

      case SCREENS.IDLE: {
        const ui = config?.ui_config || {};
        const accent = ui.accent_color || '#0F0F12';
        const textColor = ui.text_color || '#1A1A1F';
        const idleLogo = ui.idle_screen_logo_url;
        const heading = ui.idle_heading || config?.name;
        const subheading = ui.idle_subheading;
        // v3.14.0 — honor the admin-chosen logo alignment (left/center/right)
        const logoPos = ui.logo_position || 'center';
        const logoAlign = logoPos.includes('left') ? 'self-start' : logoPos.includes('right') ? 'self-end' : 'self-center';
        return (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="relative overflow-hidden flex flex-col items-center justify-center min-h-screen p-8 text-center kiosk-themed"
            style={rootThemeStyle}
            onClick={() => setScreen(SCREENS.QUANTITY_SELECT)}
            data-testid="idle-screen"
          >
            {/* Living mirror — hidden video keeps the stream warm, fades in on motion */}
            <video
              ref={idleVideoRef}
              autoPlay
              muted
              playsInline
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${mirrorActive ? 'opacity-100' : 'opacity-0'}`}
              style={{ transform: 'scaleX(-1)' }}
              data-testid="idle-mirror-video"
            />
            {mirrorActive && (
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20 pointer-events-none" />
            )}
            {lateResume && (
              <button
                onClick={claimLateResume}
                className="absolute inset-x-0 top-8 mx-auto z-20 w-fit max-w-[90%] bg-emerald-600 text-white rounded-2xl shadow-2xl px-8 py-5 flex items-center gap-4 animate-pulse"
                data-testid="late-resume-banner"
              >
                <CheckCircle className="w-9 h-9 flex-shrink-0" />
                <span className="text-left">
                  <span className="block text-xl font-semibold">Payment received — ₹{lateResume.amount}</span>
                  <span className="block text-sm text-white/85">Tap here to take your photos</span>
                </span>
              </button>
            )}
            <div className="relative z-10 flex flex-col items-center">
              {mirrorActive ? (
                <>
                  <p className="text-5xl font-serif mb-3 text-white drop-shadow-lg" data-testid="mirror-greeting">Looking good!</p>
                  <p className="text-xl text-white/85 mb-10 drop-shadow">Step in and take your photos</p>
                </>
              ) : (
                <>
                  {idleLogo ? (
                    <img src={idleLogo} alt="Venue logo" className={`max-h-24 mb-6 object-contain ${logoAlign}`} data-testid="idle-venue-logo" />
                  ) : (
                    <img src="/piczoo-logo.png" alt="Piczoo" className={`h-16 mb-6 ${logoAlign}`} />
                  )}
                  <p className="text-3xl font-serif mb-2" style={{ color: textColor }}>{heading}</p>
                  {subheading && <p className="text-lg opacity-70 mb-10" style={{ color: textColor }}>{subheading}</p>}
                </>
              )}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                animate={mirrorActive ? { scale: [1, 1.06, 1] } : {}}
                transition={mirrorActive ? { duration: 1.6, repeat: Infinity } : {}}
                className="text-white text-2xl px-16 py-6 rounded-full shadow-sharp transition-colors"
                style={{ backgroundColor: ui.button_color || accent }}
                data-testid="touch-to-start-btn"
              >
                Touch to Start
              </motion.button>
            </div>
          </motion.div>
        );
      }

      case SCREENS.QUANTITY_SELECT:
        return (
          <div className="min-h-screen p-8 flex flex-col kiosk-themed" style={rootThemeStyle}>
            <button onClick={() => setScreen(SCREENS.IDLE)} className="flex items-center gap-2 text-charcoal-800/60 mb-8">
              <ChevronLeft className="w-6 h-6" />
              Back
            </button>
            
            <div className="flex-1 flex flex-col items-center justify-center max-w-lg mx-auto">
              <h1 className="font-serif text-4xl text-charcoal-900 text-center mb-4">How Many Copies?</h1>
              <p className="text-charcoal-800/60 text-center mb-4">4 Photos per strip • 2 strips per print</p>
              
              <div className="w-full space-y-4 mb-12">
                {enabledQuantities.map((q) => {
                  const prices = activePricing || {};
                  const price = q === 1 ? prices.one_unit_price : q === 2 ? prices.two_unit_price : prices.three_unit_price;
                  return (
                    <motion.button
                      key={q}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handleSelectQuantity(q)}
                      className={`w-full p-6 rounded-2xl text-left transition-all flex items-center justify-between ${
                        quantity === q
                          ? 'bg-charcoal-900 text-white shadow-sharp'
                          : 'bg-white text-charcoal-900 hover:bg-cream-100 border-2 border-charcoal-900/10'
                      }`}
                      data-testid={`quantity-${q}-btn`}
                    >
                      <div>
                        <span className="text-2xl font-medium">{q} {q === 1 ? 'Copy' : 'Copies'}</span>
                        {q > 1 && enabledQuantities.includes(1) && (
                          <span className={`ml-3 text-sm ${quantity === q ? 'text-white/60' : 'text-green-600'}`}>
                            Save ₹{(q * (prices.one_unit_price || 50)) - price}
                          </span>
                        )}
                      </div>
                      <span className="text-3xl font-serif">₹{price}</span>
                    </motion.button>
                  );
                })}
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleProceedToPayment}
                className="btn-kiosk flex items-center justify-center gap-3"
                data-testid="proceed-to-payment-btn"
              >
                Continue to Payment
                <ChevronRight className="w-6 h-6" />
              </motion.button>
            </div>
          </div>
        );

      case SCREENS.PAYMENT:
        // v3.14.0 — Redesigned per user approval: giant scannable QR on a
        // light panel, order details on a dark side panel. QR grew from
        // 288px → ~min(56vh, 40vw) with pixelated rendering so enlarged
        // Razorpay codes keep razor-sharp edges (fixes "UPI app can't
        // read the QR" on some booths).
        return (
          <div className="fixed inset-0 flex kiosk-mode overflow-hidden">
            {paymentStatus === 'success' ? (
              // ── Success overlay — payment auto-detected via /status poll.
              // Shows for ~2s (matches the setTimeout in the poll useEffect
              // that then navigates to PHOTO_CAPTURE + startCamera).
              <div className="flex-1 flex items-center justify-center kiosk-themed" style={rootThemeStyle}>
                <motion.div
                  key="payment-success"
                  initial={{ opacity: 0, scale: 0.85 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="text-center w-full max-w-md"
                  data-testid="payment-success-screen"
                >
                  <motion.div
                    initial={{ scale: 0, rotate: -90 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ delay: 0.1, type: 'spring', stiffness: 260, damping: 18 }}
                    className="mx-auto w-32 h-32 rounded-full bg-green-500 flex items-center justify-center mb-6 shadow-[0_20px_60px_-10px_rgba(34,197,94,0.55)]"
                  >
                    <Check className="w-16 h-16 text-white stroke-[3]" strokeLinecap="round" strokeLinejoin="round" />
                  </motion.div>
                  <motion.h1
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.35 }}
                    className="font-serif text-4xl sm:text-5xl text-charcoal-900 mb-2"
                  >
                    Payment received
                  </motion.h1>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="text-charcoal-800/60 text-lg"
                  >
                    Get ready for your photos…
                  </motion.p>
                </motion.div>
              </div>
            ) : (
              <>
                {/* ── LEFT: dark info panel ── */}
                <div className="w-[36%] h-full flex flex-col justify-between p-10 xl:p-14 bg-[#0F0F12]">
                  <div>
                    <div className="text-white/40 text-xs sm:text-sm tracking-[0.25em] uppercase mb-3" data-testid="payment-brand-label">
                      {(config?.name || 'Piczoo Photobooth')}
                    </div>
                    <h1 className="font-serif text-5xl xl:text-6xl text-white leading-tight mb-8">Scan<br />to Pay</h1>
                    <div className="rounded-2xl border border-white/10 bg-white/5 p-6 mb-4">
                      <div className="flex items-end justify-between">
                        <div>
                          <div className="text-white/50 text-sm mb-1" data-testid="payment-copies-label">
                            {quantity} {quantity === 1 ? 'copy' : 'copies'}
                          </div>
                          {appliedVoucher ? (
                            <div className="flex items-end gap-3">
                              <div className="text-white text-4xl xl:text-5xl font-semibold" data-testid="payment-amount">₹{appliedVoucher.finalAmount}</div>
                              <div className="text-white/40 text-2xl line-through mb-1" data-testid="payment-original-amount">₹{getPrice()}</div>
                            </div>
                          ) : (
                            <div className="text-white text-4xl xl:text-5xl font-semibold" data-testid="payment-amount">₹{getPrice()}</div>
                          )}
                        </div>
                        <div className="text-white/40 text-xs mb-1">incl. all taxes</div>
                      </div>
                      {appliedVoucher && (
                        <div className="mt-3 flex items-center gap-2 text-emerald-400 text-sm" data-testid="voucher-applied-chip">
                          <Check className="w-4 h-4" />
                          Code {appliedVoucher.code} — you save ₹{appliedVoucher.discount}
                        </div>
                      )}
                    </div>
                    {!appliedVoucher && (
                      <button
                        onClick={() => { setVoucherOpen(true); setVoucherError(null); }}
                        className="text-white/30 hover:text-white/60 text-sm underline underline-offset-4 decoration-white/20 transition-colors mb-8 block"
                        data-testid="voucher-link"
                      >
                        Have a code?
                      </button>
                    )}
                    {appliedVoucher && <div className="mb-6" />}
                    <div className="space-y-5">
                      {['Open any UPI app', 'Scan the code', 'Photos start automatically'].map((step, i) => (
                        <div key={step} className="flex items-center gap-4">
                          <div className="w-8 h-8 rounded-full border border-white/25 text-white/80 flex items-center justify-center text-sm shrink-0">{i + 1}</div>
                          <div className="text-white/80 text-base xl:text-lg">{step}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <div className={`flex items-center gap-2 transition-colors ${paymentTimer <= 30 ? 'text-amber-400' : 'text-white/40'}`}>
                      <RefreshCw className={`w-5 h-5 ${paymentTimer <= 30 ? 'animate-pulse' : ''}`} />
                      <span
                        className={`text-xl tabular-nums ${paymentTimer <= 30 ? 'font-semibold animate-pulse' : ''}`}
                        data-testid="payment-timer"
                      >
                        {Math.floor(paymentTimer / 60)}:{(paymentTimer % 60).toString().padStart(2, '0')}
                      </span>
                    </div>
                    {confirmCancel ? (
                      <div className="flex items-center gap-2" data-testid="cancel-confirm-row">
                        <span className="text-white/60 text-sm max-w-[220px] leading-tight">Already scanned &amp; paying? Wait instead.</span>
                        <button
                          onClick={() => setConfirmCancel(false)}
                          className="text-white/70 hover:text-white text-base px-3 py-2 border border-white/20 rounded-lg"
                          data-testid="cancel-keep-waiting-btn"
                        >
                          Keep waiting
                        </button>
                        <button
                          onClick={() => {
                            const oid = orderId;
                            setConfirmCancel(false);
                            if (oid) axios.post(`${API}/payments/${oid}/cancel`).catch(() => {});
                            boothLogger.info('payment', 'user_cancel', 'Customer cancelled payment session', { order_id: oid });
                            handleReset();
                          }}
                          className="text-red-300 hover:text-red-200 text-base px-3 py-2 border border-red-400/40 rounded-lg"
                          data-testid="cancel-confirm-btn"
                        >
                          Yes, cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setConfirmCancel(true)}
                        className="flex items-center gap-1.5 text-white/40 hover:text-white/70 transition-colors text-base px-3 py-2"
                        data-testid="payment-cancel-btn"
                      >
                        ✕ Cancel &amp; start over
                      </button>
                    )}
                  </div>
                </div>

                {/* ── RIGHT: giant QR ── */}
                <div className="flex-1 h-full flex flex-col items-center justify-center kiosk-themed px-8" style={rootThemeStyle} data-testid="payment-screen">
                  <div className="relative bg-white rounded-[2rem] p-6 xl:p-8 shadow-2xl border border-black/5">
                    <div className="relative" style={{ width: 'min(56vh, 40vw)', height: 'min(56vh, 40vw)' }}>
                      {paymentTimer <= 15 && !orderError ? (
                        graceTimer !== null ? (
                          // Silent grace: visible window over but UPI can land
                          // late — keep polling 60 more seconds before reset.
                          <div
                            className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-2xl px-6 text-center"
                            data-testid="qr-grace-overlay"
                          >
                            <RefreshCw className="w-14 h-14 text-amber-500 animate-spin mb-5" />
                            <div className="text-xl font-medium text-charcoal-900 mb-1">
                              Taking a bit longer…{' '}
                              <span className="tabular-nums text-charcoal-800/50" data-testid="grace-countdown">({graceTimer}s)</span>
                            </div>
                            <div className="text-sm text-charcoal-800/50 mb-5">
                              If you&apos;ve already paid, hold on — we&apos;re still watching for it.
                            </div>
                            <button
                              onClick={() => {
                                const oid = orderId;
                                if (oid) axios.post(`${API}/payments/${oid}/cancel`).catch(() => {});
                                boothLogger.info('payment', 'grace_giveup', 'Customer gave up during grace window', { order_id: oid });
                                setGraceTimer(null);
                                handleReset();
                              }}
                              className="px-5 py-2.5 text-sm text-charcoal-800/60 border border-charcoal-900/15 rounded-lg hover:bg-cream-100"
                              data-testid="grace-giveup-btn"
                            >
                              Give up &amp; start over
                            </button>
                          </div>
                        ) : (
                        // Final 15s: QR hidden so nobody can freshly scan at the
                        // buzzer — polling continues while we "verify".
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center bg-white rounded-2xl px-6 text-center"
                          data-testid="qr-verifying-overlay"
                        >
                          <RefreshCw className="w-14 h-14 text-charcoal-900/60 animate-spin mb-5" />
                          <div className="text-xl font-medium text-charcoal-900 mb-1">
                            Verifying payment…{' '}
                            <span className="tabular-nums text-charcoal-800/50" data-testid="verify-countdown">({paymentTimer}s)</span>
                          </div>
                          <div className="text-sm text-charcoal-800/50">Checking with your bank — if you&apos;ve paid, this completes in a few seconds.</div>
                        </div>
                        )
                      ) : orderError ? (
                        // QR Retry Affordance — surfaces backend failures + lets
                        // booth staff recover without a full reboot.
                        <div
                          className="absolute inset-0 flex flex-col items-center justify-center bg-red-50 rounded-2xl px-6 text-center"
                          data-testid="qr-error-overlay"
                        >
                          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                          <div className="text-lg font-medium text-red-700 mb-1">Couldn&apos;t start payment</div>
                          <div className="text-sm text-red-600/70 mb-2 line-clamp-3">{orderError}</div>
                          <div className="text-xs text-red-500/60 mb-4" data-testid="qr-error-autoretry-note">Retrying automatically every 30 seconds…</div>
                          <button
                            onClick={() => createPaymentOrder()}
                            className="px-6 py-3 bg-red-600 text-white rounded-lg text-base font-medium hover:bg-red-700"
                            data-testid="qr-retry-btn"
                          >
                            Retry
                          </button>
                        </div>
                      ) : qrCode ? (
                        <>
                          <img
                            src={
                              // Support two formats returned by the backend:
                              //  • Real Razorpay live/test mode → full HTTPS URL like "https://rzp.io/rzp/xxx"
                              //  • Legacy mock-mode → raw base64 PNG bytes (no scheme)
                              qrCode.startsWith('http://') || qrCode.startsWith('https://') || qrCode.startsWith('data:')
                                ? qrCode
                                : `data:image/png;base64,${qrCode}`
                            }
                            alt="Payment QR Code"
                            onLoad={() => setQrImageLoaded(true)}
                            onError={() => setQrImageLoaded(true)}
                            className={`block w-full h-full object-contain transition-opacity duration-300 ${qrImageLoaded ? 'opacity-100' : 'opacity-0'}`}
                            // Pixelated upscale keeps QR module edges perfectly
                            // sharp at any size — critical for UPI scanners.
                            style={{ imageRendering: 'pixelated' }}
                            data-testid="payment-qr-code"
                          />
                          {!qrImageLoaded && <QRLoadingOverlay />}
                        </>
                      ) : (
                        <QRLoadingOverlay />
                      )}
                    </div>
                  </div>

                  <div className="mt-8 text-center">
                    <div className="text-lg xl:text-xl text-charcoal-900">
                      Works with <span className="font-semibold">GPay · PhonePe · Paytm</span> &amp; every UPI app
                    </div>
                    <p className="text-sm mt-2 text-charcoal-800/50" data-testid="payment-post-scan-hint">
                      Payment confirms automatically — allow 10–30 seconds after paying
                    </p>
                  </div>

                  {pollFailures >= 3 && (
                    <div
                      data-testid="payment-connectivity-banner"
                      className="mt-4 mx-auto max-w-md bg-amber-50 border border-amber-200 text-amber-900 px-4 py-2 rounded-xl text-sm"
                    >
                      <div className="font-medium mb-0.5">Connectivity hiccup</div>
                      <div className="text-xs text-amber-800/80">
                        Your payment is safe. We&apos;ll keep trying to confirm — please wait or call staff.
                      </div>
                    </div>
                  )}
                </div>
                {/* ── Voucher entry modal (subtle flow, touch keyboard) ── */}
                {voucherOpen && (
                  <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center" data-testid="voucher-modal">
                    <div className="bg-white rounded-3xl p-8 w-[720px] max-w-[92vw] shadow-2xl">
                      <div className="flex items-center justify-between mb-5">
                        <h3 className="font-serif text-3xl text-charcoal-900">Enter your code</h3>
                        <button
                          onClick={() => { setVoucherOpen(false); setVoucherInput(''); setVoucherError(null); }}
                          className="p-2 text-charcoal-800/40 hover:text-charcoal-800 transition-colors"
                          data-testid="voucher-close-btn"
                        >
                          <X className="w-6 h-6" />
                        </button>
                      </div>
                      <div
                        className={`h-16 rounded-xl border-2 flex items-center justify-center text-3xl font-mono tracking-[0.3em] mb-2 ${voucherError ? 'border-red-300 bg-red-50 text-red-700' : 'border-charcoal-900/15 bg-cream-50 text-charcoal-900'}`}
                        data-testid="voucher-code-display"
                      >
                        {voucherInput || <span className="text-charcoal-800/20 tracking-normal text-xl">Tap the keys below</span>}
                      </div>
                      <div className="h-6 mb-3 text-center text-sm text-red-600" data-testid="voucher-error-text">
                        {voucherError || ''}
                      </div>
                      <div className="space-y-2 mb-5 select-none">
                        {['1234567890', 'QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'].map((row, ri) => (
                          <div key={row} className="flex justify-center gap-2">
                            {row.split('').map((k) => (
                              <button
                                key={k}
                                onClick={() => { setVoucherInput((v) => (v.length < 20 ? v + k : v)); setVoucherError(null); }}
                                className="w-14 h-14 rounded-xl bg-cream-50 border border-charcoal-900/10 text-xl font-medium text-charcoal-900 active:bg-charcoal-900 active:text-white transition-colors"
                                data-testid={`voucher-key-${k}`}
                              >
                                {k}
                              </button>
                            ))}
                            {ri === 3 && (
                              <button
                                onClick={() => { setVoucherInput((v) => v.slice(0, -1)); setVoucherError(null); }}
                                className="px-5 h-14 rounded-xl bg-charcoal-900/5 border border-charcoal-900/10 text-charcoal-900 active:bg-charcoal-900 active:text-white transition-colors"
                                data-testid="voucher-key-backspace"
                              >
                                <ChevronLeft className="w-6 h-6" />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={applyVoucher}
                        disabled={!voucherInput.trim() || voucherApplying}
                        className="w-full py-4 bg-charcoal-900 text-white rounded-2xl text-xl font-medium disabled:opacity-30 transition-opacity flex items-center justify-center gap-2"
                        data-testid="voucher-apply-btn"
                      >
                        {voucherApplying ? (<><RefreshCw className="w-5 h-5 animate-spin" /> Checking…</>) : 'Apply code'}
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        );

      case SCREENS.PHOTO_CAPTURE:
        const slotAspect = getPhotoSlotAspectRatio();
        const isLandscape = slotAspect.ratio > 1;
        
        return (
          <div className="fixed inset-0 bg-charcoal-900 kiosk-mode">
            <AnimatePresence initial={false}>
              {showFlash && (
                <motion.div
                  key="capture-flash"
                  initial={{ opacity: 1 }}
                  animate={{ opacity: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-white z-50 pointer-events-none"
                />
              )}
            </AnimatePresence>

            <div className="w-full h-full flex items-center justify-center bg-black">
              <div 
                className="relative overflow-hidden rounded-lg border-4 border-white/20"
                style={{
                  aspectRatio: `${slotAspect.width} / ${slotAspect.height}`,
                  maxWidth: isLandscape ? '90vw' : '70vh',
                  maxHeight: isLandscape ? '70vh' : '90vh',
                  width: isLandscape ? '90vw' : 'auto',
                  height: isLandscape ? 'auto' : '90vh'
                }}
                data-testid="camera-preview-container"
              >
                {nativeCameraMode ? (
                  // v3.12.0 — Native camera preview: ffmpeg JPEG frames
                  // painted into an <img>. No <video>, no WebRTC surface.
                  <img
                    key="capture-native-img"
                    ref={(el) => { nativeImgRef.current = el; }}
                    alt=""
                    className="w-full h-full object-cover scale-x-[-1] bg-black"
                    data-testid="native-camera-feed"
                  />
                ) : (
                <video 
                  key="capture-video"
                  ref={(el) => {
                    // Track mount vs unmount vs remount — critical for
                    // diagnosing "stream resolved but attached to a stale
                    // element" bugs during payment→capture handoff.
                    const wasMounted = !!videoRef.current;
                    const isMounting = !!el;
                    if (wasMounted !== isMounting) {
                      boothLogger.debug('camera', 'video_ref', isMounting ? 'video element mounted' : 'video element unmounted', {
                        hasStream: !!streamRef.current,
                        streamActive: streamRef.current?.active,
                      });
                    }
                    videoRef.current = el;
                    // Ensure stream is attached when video element mounts/updates
                    if (el && streamRef.current && el.srcObject !== streamRef.current) {
                      el.srcObject = streamRef.current;
                    }
                    // Only invoke play() when video is actually paused —
                    // calling play() on an already-playing element throws
                    // AbortError("interrupted by a new load request") every
                    // time this ref callback re-runs (cosmetic noise but
                    // clutters production activity logs).
                    if (el && el.paused) {
                      el.play().catch((e) => {
                        // Ignore harmless races; anything else worth logging.
                        if (!(e && (e.name === 'AbortError' || /interrupted by/i.test(e.message || '')))) {
                          console.log('Video play error:', e);
                        }
                      });
                    }
                  }}
                  autoPlay 
                  playsInline 
                  muted 
                  className="w-full h-full object-cover scale-x-[-1]" 
                  data-testid="camera-feed" 
                />
                )}
                
                <div className="absolute top-3 left-3 bg-black/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
                  <span className="text-white text-sm font-medium">
                    {slotAspect.width}:{slotAspect.height} {isLandscape ? '(Landscape)' : slotAspect.ratio === 1 ? '(Square)' : '(Portrait)'}
                  </span>
                </div>
                
                <div className="absolute inset-0 pointer-events-none">
                  <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-white/60"></div>
                  <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-white/60"></div>
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-white/60"></div>
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-white/60"></div>
                </div>
              </div>
            </div>
            
            <canvas ref={canvasRef} className="hidden" />

            <div className="absolute inset-0 flex flex-col items-center justify-between p-8 pointer-events-none">
              <div className="w-full flex items-center justify-between pointer-events-auto">
                <div className="bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full">
                  <span className="text-white text-xl font-medium" data-testid="photo-counter">
                    Photo {currentPhotoIndex + 1} of {FIXED_PHOTO_LAYOUT.numPhotos}
                  </span>
                </div>
                <div className="bg-rose-500/90 backdrop-blur-sm px-4 py-2 rounded-full">
                  <span className="text-white text-sm font-medium">WYSIWYG Preview</span>
                </div>
              </div>

              <div className="flex-1 flex items-center justify-center">
                {countdown !== null ? (
                  <motion.div key={countdown} initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="countdown-number font-serif text-white">
                    {countdown}
                  </motion.div>
                ) : null}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={startCountdown}
                onPointerDown={() => {
                  // Independent of framer-motion's whileTap gesture — if
                  // whileTap ever swallows the click (a documented issue
                  // on some Chromium touch drivers), this still fires so
                  // the log proves the physical tap reached the DOM.
                  boothLogger.debug('input', 'capture_pointer_down', 'Capture button received pointerdown', {
                    countdown,
                    disabled: countdown !== null,
                  });
                }}
                disabled={countdown !== null}
                className="w-24 h-24 bg-white rounded-full flex items-center justify-center shadow-2xl disabled:opacity-50 pointer-events-auto"
                data-testid="capture-btn"
              >
                <Camera className="w-12 h-12 text-charcoal-900" />
              </motion.button>
            </div>
          </div>
        );

      // NEW: Photo Review Screen
      case SCREENS.PHOTO_REVIEW:
        return (
          <div className="fixed inset-0 bg-charcoal-900 flex items-center justify-center p-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="max-w-2xl w-full"
            >
              {/* Photo Counter */}
              <div className="text-center mb-6">
                <span className="bg-white/20 backdrop-blur-sm px-6 py-2 rounded-full text-white text-lg font-medium">
                  Photo {currentPhotoIndex + 1} of {FIXED_PHOTO_LAYOUT.numPhotos}
                </span>
              </div>

              {/* Photo Preview */}
              <div className="bg-white rounded-3xl p-4 shadow-2xl mb-6">
                <div className="aspect-[500/325] rounded-2xl overflow-hidden bg-charcoal-100">
                  <img 
                    src={capturedPhoto} 
                    alt="Captured photo" 
                    className="w-full h-full object-cover"
                    data-testid="captured-photo-preview"
                  />
                </div>
              </div>

              {/* Action Text */}
              <p className="text-white/80 text-center text-xl mb-8">
                Happy with this photo?
              </p>

              {/* Action Buttons */}
              <div className="flex gap-4">
                {/* Retake Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleRetakePhoto}
                  disabled={retakesLeft === 0}
                  className={`flex-1 py-5 rounded-2xl text-xl font-medium flex items-center justify-center gap-3 transition-all ${
                    retakesLeft > 0
                      ? 'bg-white/10 text-white hover:bg-white/20 border-2 border-white/30'
                      : 'bg-white/5 text-white/30 border-2 border-white/10 cursor-not-allowed'
                  }`}
                  data-testid="retake-single-btn"
                >
                  <RotateCcw className="w-6 h-6" />
                  Retake
                  {retakesLeft > 0 && (
                    <span className="text-sm bg-white/20 px-2 py-0.5 rounded-full ml-2">
                      {retakesLeft} left
                    </span>
                  )}
                </motion.button>

                {/* Keep Photo Button */}
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleKeepPhoto}
                  className="flex-1 py-5 bg-white text-charcoal-900 rounded-2xl text-xl font-medium flex items-center justify-center gap-3 shadow-lg"
                  data-testid="keep-photo-btn"
                >
                  <Check className="w-6 h-6" />
                  {currentPhotoIndex + 1 < FIXED_PHOTO_LAYOUT.numPhotos ? 'Keep & Next' : 'Preview & Print'}
                </motion.button>
              </div>

              {/* Photo Progress Dots */}
              <div className="flex justify-center gap-2 mt-8">
                {Array.from({ length: FIXED_PHOTO_LAYOUT.numPhotos }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-3 h-3 rounded-full transition-all ${
                      i < photos.length
                        ? 'bg-green-500'
                        : i === currentPhotoIndex
                        ? 'bg-white'
                        : 'bg-white/30'
                    }`}
                  />
                ))}
              </div>
            </motion.div>
          </div>
        );

      case SCREENS.PREVIEW:
      case SCREENS.PRINTING:
      case SCREENS.COMPLETE: {
        // Unified Print & Collect screen (Jun 2026 flow simplification): one
        // continuous layout from printing through collect — the strip preview
        // and QR panel (download + WhatsApp help) stay in place from the very
        // start; only the header and bottom status zone change between phases.
        const phase = screen === SCREENS.COMPLETE ? 'collect' : 'printing';
        const templateConfig = config?.template_config || { backgroundColor: '#FFFFFF', customLogoUrl: null };
        const staged = phase === 'printing' && printStages && printStages.copies > 1 ? (() => {
          // Stage 1 spans base overhead + first sheet; later sheets queue
          // back-to-back so each later stage is just perCopy long.
          const elapsed = Math.max(0, (Date.now() - printStages.startedAt) / 1000);
          const firstLen = (printStages.base || 0) + printStages.perCopy;
          const cur = elapsed < firstLen
            ? 0
            : Math.min(1 + Math.floor((elapsed - firstLen) / printStages.perCopy), printStages.copies - 1);
          const stageStart = cur === 0 ? 0 : firstLen + (cur - 1) * printStages.perCopy;
          const stageLen = cur === 0 ? firstLen : printStages.perCopy;
          const copyPct = Math.min(100, ((elapsed - stageStart) / stageLen) * 100);
          return { cur, copyPct, remaining: printStages.copies - cur - 1 };
        })() : null;
        return (
          <div className="fixed inset-0 flex flex-col items-center justify-center kiosk-themed overflow-hidden px-8" style={rootThemeStyle} data-testid={phase === 'collect' ? 'complete-screen' : 'printing-screen'}>
            <div className="flex items-center gap-3 mb-5">
              {phase === 'collect' ? (
                <>
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10 }}>
                    <Check className="w-10 h-10 text-green-600" />
                  </motion.div>
                  <h1 className="font-serif text-4xl xl:text-5xl text-charcoal-900" data-testid="collect-title">Collect Your Photos!</h1>
                </>
              ) : (
                <>
                  <motion.div animate={{ y: [0, -6, 0] }} transition={{ duration: 1.2, repeat: Infinity }}>
                    <Printer className="w-10 h-10 text-charcoal-900" />
                  </motion.div>
                  <h1 className="font-serif text-4xl xl:text-5xl text-charcoal-900" data-testid="printing-title">
                    {staged ? `Printing copy ${staged.cur + 1} of ${printStages.copies}…` : 'Printing your strips…'}
                  </h1>
                </>
              )}
            </div>

            {stripPreview ? (
              <div className="flex items-center justify-center gap-10 flex-wrap">
                <div className="flex gap-1 rounded-lg overflow-hidden shadow-2xl p-1" style={{ backgroundColor: templateConfig.backgroundColor }} data-testid="exact-print-preview">
                  {[0, 1].map((stripIndex) => (
                    <img
                      key={stripIndex}
                      src={stripPreview}
                      alt={`Print strip ${stripIndex + 1}`}
                      style={{ height: '52vh', width: 'auto' }}
                    />
                  ))}
                </div>
                {(downloadQrCode || helpQr) && (
                  <motion.div
                    initial={{ opacity: 0, x: 24 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex flex-col gap-4 items-center"
                  >
                    {downloadQrCode && (
                      <div className="bg-white rounded-2xl p-5 shadow-2xl text-center" data-testid="printing-qr-card">
                        <div className="flex items-center justify-center gap-2 mb-2">
                          <Smartphone className="w-4 h-4 text-charcoal-800/60" />
                          <span className="text-charcoal-800 font-medium text-sm">Get Digital Copy</span>
                        </div>
                        <img
                          src={`data:image/png;base64,${downloadQrCode}`}
                          alt="Download QR"
                          style={{ width: '21vh', height: '21vh' }}
                          className="mx-auto"
                          data-testid="download-qr-code"
                        />
                        <p className="text-charcoal-900 font-medium mt-2">Scan for your photos</p>
                        <p className="text-charcoal-800/50 text-xs mt-0.5">Available for 48 hours</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </div>
            ) : (
              <div className="flex items-center justify-center rounded-lg bg-white/60 shadow-2xl" style={{ height: '52vh', width: '35vh' }}>
                <RefreshCw className="w-10 h-10 text-charcoal-900/40 animate-spin" />
              </div>
            )}

            {/* Help QR tucked into the bottom-left corner, far from the download QR
                (people kept scanning it by mistake) — smaller + visually muted */}
            {helpQr && (
              <div className="fixed bottom-16 left-4 bg-white/85 backdrop-blur-sm rounded-xl shadow-md border border-charcoal-900/10 p-2.5 flex items-center gap-2.5" data-testid="whatsapp-help-card">
                <img
                  src={`data:image/png;base64,${helpQr}`}
                  alt="WhatsApp help QR"
                  className="w-24 h-24"
                  data-testid="whatsapp-help-qr"
                />
                <div className="text-left pr-1">
                  <div className="flex items-center gap-1">
                    <MessageCircle className="w-4 h-4 text-green-600" />
                    <span className="text-xs font-semibold text-charcoal-900/80">Print problem?</span>
                  </div>
                  <p className="text-[11px] text-charcoal-800/50 leading-snug mt-0.5">
                    Staff help — WhatsApp us
                  </p>
                </div>
              </div>
            )}

            {phase === 'collect' ? (
              <div className="flex flex-col items-center mt-5" data-testid="collect-zone">
                {quantity > 1 && (
                  <p className="text-lg font-semibold text-charcoal-900 bg-amber-100 border border-amber-200 rounded-xl px-5 py-2" data-testid="collect-count-reminder">
                    Collect all {quantity} prints ({quantity * 2} strips) from the tray
                  </p>
                )}
                <div className="flex items-center gap-2 mt-3">
                  <span className="text-sm text-charcoal-800/50">Thanks for using</span>
                  <img src="/piczoo-logo.png" alt="Piczoo" className="h-4" />
                </div>
                <p className="text-charcoal-800/40 text-sm mt-1">Returning to start in {completeTimer} seconds...</p>
              </div>
            ) : staged ? (
              <div className="flex flex-col items-center mt-6" data-testid="staged-print-tracker">
                <div className="flex items-center gap-3 mb-4">
                  {Array.from({ length: printStages.copies }, (_, i) => (
                    <div
                      key={i}
                      className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium ${
                        i < staged.cur ? 'bg-green-100 text-green-800'
                        : i === staged.cur ? 'bg-charcoal-900 text-white'
                        : 'bg-charcoal-900/10 text-charcoal-800/50'
                      }`}
                      data-testid={`print-stage-${i + 1}`}
                    >
                      {i < staged.cur ? <Check className="w-4 h-4" /> : i === staged.cur ? <RefreshCw className="w-4 h-4 animate-spin" /> : null}
                      Print {i + 1}
                    </div>
                  ))}
                </div>
                <div className="w-80 h-2 bg-charcoal-900/10 rounded-full overflow-hidden">
                  <div className="h-full bg-charcoal-900 rounded-full transition-[width] duration-1000 ease-linear" style={{ width: `${staged.copyPct}%` }} />
                </div>
                <p className="text-2xl font-semibold text-charcoal-900 mt-5" data-testid="dont-leave-warning">
                  {staged.remaining > 0
                    ? `Please don't leave yet — ${staged.remaining} more print${staged.remaining > 1 ? 's' : ''} after this one!`
                    : 'Almost done — your final print is coming out!'}
                </p>
                <p className="text-lg text-charcoal-800/60 mt-2">
                  Each print = 2 strips · {printStages.copies * 2} strips in total
                </p>
              </div>
            ) : (
              <>
                <div className="w-64 h-2 bg-charcoal-900/10 rounded-full mt-7 overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: '100%' }} transition={{ duration: 5, ease: 'linear' }} className="h-full bg-charcoal-900 rounded-full" />
                </div>
                <p className="text-lg text-charcoal-800/60 mt-3">You&apos;ll receive 2 identical strips — please collect them below</p>
              </>
            )}
          </div>
        );
      }

      case SCREENS.DISCONNECTED:
        return (
          <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-red-50">
            <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', damping: 10 }}>
              <WifiOff className="w-32 h-32 text-red-500 mb-8" />
            </motion.div>
            <h1 className="font-serif text-4xl text-charcoal-900 mb-4">Kiosk Disconnected</h1>
            <p className="text-xl text-charcoal-800/60 mb-8 text-center max-w-md">
              This kiosk has been disconnected by the administrator. Please contact support or re-pair the kiosk.
            </p>
            <button
              onClick={() => { setScreen(SCREENS.PAIRING); setPairingCode(''); setPairingError(''); }}
              className="btn-kiosk"
              data-testid="reconnect-btn"
            >
              Re-pair Kiosk
            </button>
          </div>
        );

      case SCREENS.ERROR:
        return (
          <ErrorDisplay
            title={currentError?.title || 'Error'}
            message={currentError?.message || 'Something went wrong.'}
            action={currentError?.action || 'Please try again.'}
            type={currentError?.type || ERROR_TYPES.UNKNOWN}
            code={currentError?.code}
            technicalDetails={currentError?.technicalDetails}
            countdown={currentError?.type === ERROR_TYPES.CAMERA ? cameraRetryCountdown : null}
            onRetry={handleErrorRetry}
            retryText="Try Again"
            onCancel={handleReset}
            cancelText="Start Over"
            fullScreen={true}
          />
        );

      default:
        return null;
    }
  };

  return (
    <ErrorBoundary onReset={handleReset}>
      <div className="kiosk-mode relative" data-testid="kiosk-app">
        {/* Network Status Banner */}
        <NetworkStatusBanner isOnline={isOnline} />
        
        {/* Session Timeout Warning */}
        {showTimeoutWarning && (
          <SessionTimeoutWarning
            remainingSeconds={timeoutRemaining}
            onContinue={handleSessionContinue}
            onTimeout={handleReset}
          />
        )}
        
        {renderScreen()}
        
        {/* Connection status indicator */}
        {screen !== SCREENS.PAIRING && screen !== SCREENS.RECONNECTING && screen !== SCREENS.DISCONNECTED && screen !== SCREENS.ERROR && (
          <div className={`fixed bottom-4 left-4 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs z-50 ${
            wsConnected ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
          }`}>
            {wsConnected ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {wsConnected ? 'Connected' : 'Reconnecting...'}
          </div>
        )}
        
        {/* v3.14.0 (user-approved) — Hidden exit: NO visible button. Staff
            tap the top-left corner 5 times within 2 seconds to open the
            password-protected exit modal. Customers see nothing.
            v3.14.4: window-level coordinate listener instead of an invisible
            overlay div — the old overlay sat ON TOP of the quantity screen's
            Back button and swallowed its taps. */}

        {/* Exit Modal */}
        {showExitModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-charcoal-900/80">
            <div className="bg-white rounded-2xl p-8 max-w-sm w-full" data-testid="exit-modal">
              <h3 className="font-serif text-2xl text-charcoal-900 mb-4">Exit Kiosk Mode</h3>
              <p className="text-charcoal-800/60 mb-6">Enter admin password to exit</p>
              
              <input
                type="password"
                value={exitPassword}
                onChange={(e) => setExitPassword(e.target.value)}
                placeholder="Password"
                className="w-full px-4 py-3 bg-cream-50 border border-charcoal-900/10 rounded-xl mb-4"
                data-testid="exit-password-input"
              />
              
              {exitError && <p className="text-red-500 text-sm mb-4">{exitError}</p>}
              
              <div className="flex gap-3">
                <button onClick={() => { setShowExitModal(false); setExitPassword(''); setExitError(''); }} className="flex-1 py-3 border border-charcoal-900/10 rounded-xl">
                  Cancel
                </button>
                <button onClick={handleExit} className="flex-1 py-3 bg-charcoal-900 text-white rounded-xl" data-testid="confirm-exit-btn">
                  Exit
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Operator Error Console — discreet floating button bottom-right.
            Shows ALL recent errors so venue staff can debug on-site without
            opening the admin dashboard. Hidden during pairing/disconnected
            because the screen is replaced wholesale anyway. */}
        {screen !== SCREENS.PAIRING && screen !== SCREENS.RECONNECTING && screen !== SCREENS.DISCONNECTED && (
          <ErrorConsole errors={bufferedErrors} onClear={clearBufferedErrors} />
        )}
      </div>
    </ErrorBoundary>
  );
};

const KioskAppWithBoundary = () => (
  <KioskErrorBoundary>
    <KioskApp />
  </KioskErrorBoundary>
);

export default KioskAppWithBoundary;
;
